/** @jsxImportSource react */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_THEME = 'app_theme';
const STORAGE_LANG = 'app_lang';

export type ThemeId = 'default' | 'violet' | 'light-pure' | 'violet-inverted' | 'black';
export type LangId = 'es' | 'en';

function readTheme(): ThemeId {
  try {
    const v = localStorage.getItem(STORAGE_THEME);
    if (v === 'violet' || v === 'light-pure' || v === 'violet-inverted' || v === 'black') return v as ThemeId;
    if (v === 'violet-deep' || v === 'violet-soft' || v === 'light-neutral') return 'violet'; // migrar temas antiguos
  } catch (_) { }
  return 'default';
}

function readLang(): LangId {
  try {
    const v = localStorage.getItem(STORAGE_LANG);
    if (v === 'en') return 'en';
  } catch (_) { }
  return 'es';
}

type ThemeLanguageContextValue = {
  theme: ThemeId;
  setTheme: (id: ThemeId) => void;
  lang: LangId;
  setLang: (id: LangId) => void;
  /** Translate key. Optional second arg: { [key]: value } to replace {{key}} in the string. */
  t: (key: string, vars?: Record<string, string>) => string;
};

const translations: Record<LangId, Record<string, string>> = {
  es: {
    'sidebar.panel': 'Panel de Vacantes',
    'sidebar.new': 'Carga de Vacante',
    'sidebar.premium': 'Premium',
    'sidebar.settings': 'Configuración',
    'sidebar.backToAdmin': 'Panel de administración',
    'sidebar.account': 'Cuenta',
    'sidebar.logout': 'Cerrar sesión',
    'sidebar.closing': 'Cerrando...',
    'privacy.trigger': 'Aviso de Privacidad',
    'privacy.modalTitle': 'Aviso de Privacidad',
    'privacy.modalSubtitle': 'Plataforma de Reclutamiento IA',
    'privacy.closeLabel': 'Cerrar aviso de privacidad',
    'privacy.section1Title': 'Responsable del Tratamiento',
    'privacy.section1Text': 'AI Recruiter es la entidad responsable del tratamiento de los datos personales que proporcionas durante tu proceso de reclutamiento a través de esta plataforma.',
    'privacy.section2Title': 'Datos que Recopilamos',
    'privacy.section2Intro': 'Durante el proceso de aplicación se recopilan los siguientes datos personales:',
    'privacy.section2Item1': 'Nombre completo y correo electrónico (a través de tu cuenta de Google)',
    'privacy.section2Item2': 'Fotografía de perfil de tu cuenta de Google',
    'privacy.section2Item3': 'Currículum Vitae (CV) en formato PDF o DOCX',
    'privacy.section2Item4': 'Información extraída de tu CV mediante Inteligencia Artificial (experiencia laboral, educación, habilidades, etc.)',
    'privacy.section2Item5': 'Video de presentación personal',
    'privacy.section2Item6': 'Respuestas a preguntas de entrevista',
    'privacy.section2Item7': 'Información de disponibilidad y agenda de Google Calendar',
    'privacy.section3Title': 'Finalidad del Tratamiento',
    'privacy.section3Intro': 'Tus datos personales serán utilizados exclusivamente para:',
    'privacy.section3Item1': 'Evaluar tu perfil profesional para la vacante a la que aplicas',
    'privacy.section3Item2': 'Procesar y analizar tu CV mediante herramientas de Inteligencia Artificial',
    'privacy.section3Item3': 'Generar una puntuación de compatibilidad con el perfil de la vacante',
    'privacy.section3Item4': 'Facilitar la comunicación entre tú y el reclutador',
    'privacy.section3Item5': 'Agendar entrevistas a través de Google Calendar',
    'privacy.section4Title': 'Uso de Inteligencia Artificial',
    'privacy.section4Text': 'Esta plataforma utiliza modelos de Inteligencia Artificial (Google Gemini) para analizar tu CV, generar preguntas de entrevista personalizadas y evaluar tus respuestas. La información procesada por IA se utiliza únicamente como herramienta de apoyo en el proceso de selección, y las decisiones finales de contratación siempre son tomadas por personas.',
    'privacy.section5Title': 'Transferencia de Datos',
    'privacy.section5Text': 'Tus datos pueden ser compartidos con los servicios de terceros necesarios para el funcionamiento de la plataforma: Google (autenticación, calendar e IA), Firebase (almacenamiento seguro) y Stripe (procesamiento de pagos del reclutador). No venderemos ni compartiremos tu información personal con terceros con fines comerciales.',
    'privacy.section6Title': 'Conservación de Datos',
    'privacy.section6Text': 'Tus datos personales se conservarán durante el tiempo necesario para completar el proceso de reclutamiento de la vacante a la que aplicaste. Puedes solicitar la eliminación de tus datos en cualquier momento contactándonos directamente.',
    'privacy.section7Title': 'Derechos ARCO',
    'privacy.section7Intro': 'Como titular de tus datos personales, tienes derecho a:',
    'privacy.section7Access': 'Acceso:',
    'privacy.section7AccessDesc': ' Conocer qué datos tenemos sobre ti',
    'privacy.section7Rectification': 'Rectificación:',
    'privacy.section7RectificationDesc': ' Corregir datos inexactos o incompletos',
    'privacy.section7Cancellation': 'Cancelación:',
    'privacy.section7CancellationDesc': ' Solicitar la eliminación de tus datos',
    'privacy.section7Opposition': 'Oposición:',
    'privacy.section7OppositionDesc': ' Oponerte al tratamiento de tus datos',
    'privacy.section7Contact': 'Para ejercer estos derechos, puedes contactarnos a través del correo electrónico proporcionado por la empresa responsable del proceso de reclutamiento.',
    'privacy.section8Title': 'Seguridad',
    'privacy.section8Text': 'Tus datos personales son almacenados de forma segura utilizando Google Firebase, con cifrado en tránsito y en reposo. Implementamos medidas de seguridad administrativas, técnicas y físicas para proteger tu información contra acceso no autorizado, pérdida o alteración.',
    'privacy.section9Title': 'Consentimiento',
    'privacy.section9Text': 'Al continuar con el proceso de aplicación y subir tu CV, video o responder las preguntas, aceptas los términos de este aviso de privacidad y consientes el tratamiento de tus datos personales conforme a lo aquí descrito.',
    'privacy.lastUpdate': 'Última actualización: Febrero 2026',
    'settings.tab.profile': 'Perfil',
    'settings.tab.notifications': 'Notificaciones',
    'settings.tab.privacy': 'Privacidad',
    'settings.tab.theme': 'Tema',
    'settings.tab.language': 'Idioma',
    'settings.loading': 'Cargando configuración...',
    'settings.profile.noName': 'Sin nombre',
    'settings.profile.noEmail': 'Sin email',
    'settings.notifications.soon': 'Opciones de notificaciones por email (próximamente).',
    'settings.privacy.soon': 'Gestiona tu privacidad y datos (próximamente).',
    'settings.theme.title': 'Apariencia',
    'settings.theme.default': 'Por defecto (azul)',
    'settings.theme.violet': 'Violeta',
    'settings.theme.lightPure': 'Blanco puro',
    'settings.theme.violetInverted': 'Violeta invertido',
    'settings.theme.black': 'Negro puro',
    'settings.language.title': 'Idioma',
    'settings.language.es': 'Español',
    'settings.language.en': 'English',
    'title.panel': 'Panel de Vacantes',
    'title.new': 'Carga de Vacante',
    'title.checkout': 'Premium',
    'title.settings': 'Configuración',
    'title.edit': 'Editar vacante',
    'title.workspace': 'Workspace',
    'title.candidatesList': 'Lista de candidatos',
    'title.questions': 'Preguntas de la vacante',
    'title.metrics': 'Métricas',
    'nav.backToEdit': 'Volver a editar vacante',
    'nav.backToPanel': 'Volver al panel',
    'dashboard.intro': 'Gestiona tus vacantes. Filtra, cambia estado y accede al workspace o a las preguntas desde aquí.',
    'dashboard.loading': 'Cargando vacantes...',
    'dashboard.retry': 'Reintentar',
    'dashboard.noResultsHint': 'No hay vacantes que coincidan con los filtros.',
    'dashboard.titleColumn': 'Título',
    'dashboard.filterSearch': 'Buscar por título...',
    'dashboard.statusColumn': 'Estado',
    'dashboard.filterAll': 'Todos',
    'dashboard.createdAt': 'Fecha de creación',
    'dashboard.filterAllDates': 'Sin orden',
    'dashboard.filterDateOldest': 'Más antiguos primero',
    'dashboard.filterDateNewest': 'Más recientes primero',
    'dashboard.updatedAt': 'Última actualización',
    'dashboard.actions': 'Acciones',
    'dashboard.errorUpdate': 'No se pudo actualizar',
    'dashboard.deleteSuccess': 'Vacante "{{title}}" eliminada correctamente.',
    'dashboard.errorDelete': 'No se pudo eliminar la vacante. Inténtalo de nuevo.',
    'dashboard.tooltipWorkspace': 'Ver candidatos de esta vacante',
    'dashboard.tooltipMetrics': 'Ver métricas de esta vacante',
    'dashboard.tooltipQuestions': 'Editar preguntas de la entrevista',
    'dashboard.tooltipCopyUrl': 'Copiar URL pública',
    'dashboard.tooltipEdit': 'Editar vacante',
    'dashboard.tooltipDelete': 'Eliminar vacante',
    'dashboard.tooltipFilterSearch': 'Buscar por título de puesto',
    'dashboard.linkCopied': 'Link copiado',
    'dashboard.noTitle': 'Sin título',
    'dashboard.tooltipStatus': 'Cambiar estado de la vacante',
    'dashboard.tooltipFilterStatus': 'Filtrar por estado',
    // ──── Admin ────
    'admin.redirecting': 'Redirigiendo...',
    'admin.account': 'Cuenta',
    'admin.loading': 'Cargando...',
    'admin.dashboard': 'Dashboard',
    'admin.users': 'Usuarios',
    'admin.vacancies': 'Vacantes',
    'admin.config': 'Configuración',
    'admin.audit': 'Auditoría',
    'admin.aiUsage': 'Uso IA',
    'admin.notifications': 'Notificaciones',
    'admin.markAllRead': 'Marcar todo como leído',
    'admin.noNotifications': 'No hay notificaciones',
    'admin.breadcrumb': 'Panel de Administración',
    'admin.sidebar.expand': 'Expandir menú',
    'admin.sidebar.collapse': 'Colapsar menú',
    'admin.logout': 'Cerrar sesión',
    'admin.dashboard.title': 'Dashboard Administrativo',
    'admin.dashboard.cardCandidates': 'Candidatos',
    'admin.dashboard.cardVacancies': 'Vacantes',
    'admin.dashboard.cardRecruiters': 'Reclutadores',
    'admin.dashboard.cardApplications': 'Aplicaciones',
    'admin.dashboard.suspendedAlert': '¡Atención! Hay {{count}} usuarios suspendidos que requieren revisión.',
    'notification.welcomeAdminTitle': 'Panel de administración',
    'notification.welcomeAdminMessage': 'Aquí verás notificaciones de la plataforma (nuevas aplicaciones, etc.).',
    'notification.welcomeRecruiterTitle': 'Bienvenido a AI Recruiter',
    'notification.welcomeRecruiterMessage': 'Saity te ayudará durante todo tu proceso con IA.',
    'admin.dashboard.weeklyTrends': 'Tendencias semanales',
    'admin.dashboard.colWeek': 'Semana',
    'admin.dashboard.colCandidates': 'Candidatos',
    'admin.dashboard.colVacancies': 'Vacantes',
    'admin.dashboard.colApplications': 'Aplicaciones',
    'admin.users.title': 'Gestión de Usuarios',
    'admin.users.filterEmail': 'Email',
    'admin.users.filterUid': 'UID',
    'admin.users.filterAllRoles': 'Todos los roles',
    'admin.users.roleCandidate': 'Candidato',
    'admin.users.roleRecruiter': 'Reclutador',
    'admin.users.roleAdmin': 'Admin',
    'admin.users.filterAllStatuses': 'Todos los estados',
    'admin.users.statusActive': 'Activo',
    'admin.users.statusSuspended': 'Suspendido',
    'admin.users.search': 'Buscar',
    'admin.users.columnRole': 'Rol',
    'admin.users.columnEmail': 'Email',
    'admin.users.columnName': 'Nombre',
    'admin.users.columnStatus': 'Estado',
    'admin.users.columnActions': 'Acciones',
    'admin.users.activate': 'Activar',
    'admin.users.suspend': 'Suspender',
    'admin.users.impersonate': 'Personificar',
    'admin.users.impersonateTitle': 'Iniciar sesión como este usuario',
    'admin.users.changeRole': 'Cambiar rol',
    'admin.users.rolePending': 'Pendiente de asignar',
    'admin.users.ownerTitle': 'Titular de la instancia. Es el único que puede nombrar o destituir administradores. Para cambiar su rol hay que traspasar antes la titularidad.',
    'admin.users.transfer': 'Hacer titular',
    'admin.users.transferTitle': 'Traspasar la titularidad de la instancia a esta persona',
    'admin.users.transferConfirm': '¿Traspasar la titularidad de la instancia a {email}? Dejarás de ser el titular, aunque seguirás siendo administrador.',
    'pending.title': 'Tu cuenta está pendiente de aprobación',
    'pending.lead': 'Ya entraste correctamente, pero todavía no tienes un rol asignado en la plataforma.',
    'pending.hint': 'Pídele a un administrador que te asigne un rol. En cuanto lo haga, vuelve a iniciar sesión para que tome efecto.',
    'pending.signOut': 'Cerrar sesión',
    'admin.users.roleLockedTitle': 'Es administrador por la variable ADMIN_EMAILS del despliegue. Para cambiarlo, edita esa variable.',
    'admin.impersonation.label': 'Viendo como usuario: {{uid}}',
    'admin.impersonation.exit': 'Salir',
    'admin.config.title': 'Configuración del Sistema',
    'admin.config.tabGeneral': 'General',
    'admin.config.tabNotifications': 'Notificaciones',
    'admin.config.featureFlags': 'Feature flags',
    'admin.config.maintenanceMode': 'Modo mantenimiento',
    'admin.config.newCandidateFlow': 'Nuevo flujo candidato',
    'admin.config.limitsFree': 'Límites (plan free)',
    'admin.config.limitVacancies': 'Máximo de vacantes por reclutador (free)',
    'admin.config.generalKeyValue': 'General (clave-valor)',
    'admin.config.noKeys': 'No hay otras claves. Añade una abajo si lo necesitas.',
    'admin.config.newKeyPlaceholder': 'Nueva clave',
    'admin.config.newValuePlaceholder': 'Valor (texto o JSON)',
    'admin.config.add': 'Añadir',
    'admin.config.saving': 'Guardando...',
    'admin.config.save': 'Guardar',
    'admin.config.savedMsg': 'Configuración guardada.',
    'admin.config.notifPlatformTitle': 'Notificaciones de la plataforma',
    'admin.config.notifDescription': 'Resumen de notificaciones (nuevas aplicaciones, avisos del sistema). La campana del header muestra el resumen; aquí puedes ver el detalle completo.',
    'admin.config.deleteAll': 'Borrar todas',
    'admin.config.deleteConfirm': '¿Borrar todas las notificaciones?',
    'admin.config.markRead': 'Marcar leída',
    'admin.config.labelVacancy': 'Vacante',
    'admin.config.labelIdVacancy': 'ID vacante',
    'admin.config.labelCandidate': 'Candidato',
    'admin.config.labelEmail': 'Email',
    'admin.config.labelIdApp': 'ID aplicación',
    'admin.config.goToPanel': 'Ir al panel',
    'admin.audit.title': 'Registro de Auditoría',
    'admin.audit.filterAdminEmail': 'Admin (email)',
    'admin.audit.filterAllActions': 'Todas las acciones',
    'admin.audit.filterFrom': 'Desde',
    'admin.audit.filterTo': 'Hasta',
    'admin.audit.search': 'Buscar',
    'admin.audit.exportCsv': 'Exportar CSV',
    'admin.audit.colDate': 'Fecha',
    'admin.audit.colAdmin': 'Admin',
    'admin.audit.colAction': 'Acción',
    'admin.audit.colTarget': 'Objetivo',
    'admin.audit.colIp': 'IP',
    'admin.audit.prev': 'Anterior',
    'admin.audit.next': 'Siguiente',
    'admin.aiMonitoring.title': 'Monitoreo de IA',
    'admin.aiMonitoring.summary': 'Registro de llamadas a Gemini (tokens por llamada). Esta página: {{total}} tokens.',
    'admin.aiMonitoring.colDate': 'Fecha',
    'admin.aiMonitoring.colModel': 'Modelo',
    'admin.aiMonitoring.colCaller': 'Caller',
    'admin.aiMonitoring.colInput': 'Input',
    'admin.aiMonitoring.colOutput': 'Output',
    'admin.aiMonitoring.colTotal': 'Total',
    'admin.aiMonitoring.colError': 'Error',
    'common.errorUnknown': 'Ocurrió un error. Inténtalo de nuevo.',
    'common.confirmDelete': 'Confirmar eliminación',
    'common.errorLoad': 'No se pudo cargar. Comprueba tu conexión e inténtalo de nuevo.',
    'common.errorSave': 'No se pudo guardar. Comprueba tu conexión e inténtalo de nuevo.',
    'modal.cancel': 'Cancelar',
    'modal.cannotUndo': 'Esta acción no se puede deshacer.',
    'modal.delete': 'Eliminar',
    'modal.deleteVacancyConfirm': '¿Eliminar la vacante "{{title}}"?',
    'modal.deleteQuestionConfirm': '¿Eliminar esta pregunta?',
    'modal.savingChanges': 'Guardando cambios...',
    'modal.savedSuccess': 'Cambios guardados correctamente.',
    'modal.logoutTitle': 'Cerrar sesión',
    'modal.logoutMessage': '¿Estás seguro de que deseas cerrar sesión?',
    'modal.logoutConfirm': 'Sí, cerrar sesión',
    'modal.vacancySavedSuccess': 'Vacante guardada con éxito',
    'modal.questionsSavedSuccess': 'Preguntas guardadas con éxito',
    'modal.done': 'Listo',
    'editor.statusDraft': 'Borrador',
    'editor.statusActive': 'Activo',
    'editor.statusPaused': 'Pausado',
    'editor.statusClosed': 'Cerrado',
    'editor.statusLabel': 'Estado',
    'editor.savingDraft': 'Guardando borrador...',
    'editor.publishing': 'Publicando vacante...',
    'editor.pausing': 'Pausando vacante...',
    'editor.closing': 'Cerrando vacante...',
    'editor.saving': 'Guardando...',
    'editor.saveChanges': 'Guardar Cambios',
    'editor.successDraft': 'Borrador guardado',
    'editor.successPublished': 'Vacante publicada',
    'editor.successPaused': 'Vacante pausada',
    'editor.successClosed': 'Vacante cerrada',
    'editor.errorSaveHint': 'Error al guardar.',
    'editor.positionTitle': 'Título de la posición',
    'editor.vacancySummary': 'Resumen de la vacante',
    'editor.intro': 'Revisa y completa los datos. Elige el estado (Borrador, Activo, Pausado o Cerrado) y guarda. Luego podrás configurar las preguntas de la entrevista.',
    'editor.hintFirstSteps': 'Empieza por subir un JD en "Cargar vacante" o crea una desde el panel.',
    'editor.dataLoadedBetter': 'Hemos extraído los datos del documento. Revisa y corrige lo que necesites antes de publicar.',
    'editor.softSkills': 'Habilidades blandas',
    'editor.location': 'Ubicación',
    'editor.modality': 'Modalidad',
    'editor.modalityPresencial': 'Presencial',
    'editor.modalityRemoto': 'Remoto',
    'editor.modalityHibrido': 'Híbrido',
    'editor.salaryMin': 'Salario Mínimo',
    'editor.salaryMax': 'Salario Máximo',
    'editor.currency': 'Moneda',
    'editor.salaryConfidential': 'Mostrar salario como "Confidencial" al candidato',
    'editor.experienceYears': 'Experiencia Requerida (Años)',
    'editor.hardSkills': 'Habilidades Técnicas (Duras)',
    'editor.placeholderTitle': 'Ej: Senior Full Stack Developer',
    'editor.placeholderSummary': 'Describe brevemente la vacante...',
    'editor.placeholderLocation': 'Ej: Ciudad de México, CDMX',
    'editor.placeholderSalaryMin': 'Ej: 30000',
    'editor.placeholderSalaryMax': 'Ej: 45000',
    'editor.placeholderSkills': 'Separa con saltos de línea. Ej: Python\\nSQL\\nAWS',
    'editor.noTitle': 'Sin Título',
    'editor.waitingAnalysis': 'Esperando resultados del análisis IA y conexión con el Backend.',
    'editor.copyUrl': 'Copiar URL',
    'editor.copied': '¡Copiado!',
    'editor.copyUrlDraft': 'Publica la vacante antes de compartir el enlace: en borrador, quien lo abra verá que no está disponible.',
    'editor.errorNoId': 'Error: No se proporcionó ID de vacante',
    'editor.loadingVacancy': 'Cargando datos de la vacante...',
    'editor.loadSuccess': 'Datos cargados correctamente. Puedes editar la vacante.',
    'editor.copyUrlPrompt': 'Copia la URL:',
    'editor.placeholderSoftSkills': 'Ej: Comunicación\nLiderazgo',
    'editor.errorLoadHint': 'Error al cargar:',
    'questions.generatedCount': 'Preguntas Generadas ({{count}})',
    'questions.difficulty': 'Dificultad',
    'questions.loading': 'Cargando preguntas...',
    'questions.questionType': 'Tipo de pregunta:',
    'questions.typeLabel': 'Tipo:',
    'questions.typeTechnical': 'Técnica',
    'questions.typeBehavioral': 'Comportamiento',
    'questions.typeCulture': 'Cultura',
    'questions.difficultyEasy': 'Fácil',
    'questions.difficultyMedium': 'Medio',
    'questions.difficultyHard': 'Difícil',
    'questions.editQuestion': 'Editar esta pregunta',
    'questions.placeholderQuestion': 'Escribe la pregunta...',
    'questions.noQuestionsYet': 'No hay preguntas generadas aún. Las preguntas se generan automáticamente cuando guardas la vacante. Si acabas de guardar, espera unos segundos y recarga la página.',
    'questions.regenerating': 'Regenerando preguntas...',
    'questions.regenerateSuccess': '✅ Nuevas preguntas generadas. Por favor, revísalas y aprueba si te agradan.',
    'questions.deleteQuestion': 'Eliminar esta pregunta',
    'questions.deleteError': 'Error al eliminar la pregunta. Por favor, intenta de nuevo.',
    'questions.loadError': 'Error al cargar las preguntas. Por favor, intenta de nuevo.',
    'questions.saving': 'Guardando preguntas...',
    'questions.regenerateError': 'Error al regenerar preguntas.',
    'questions.addQuestionError': 'Error al generar nueva pregunta.',
    'questions.deleteErrorShort': 'Error al eliminar pregunta.',
    'questions.savingChanges': 'Guardando cambios...',
    'questions.updateError': 'Error al actualizar pregunta.',
    'questions.deleteSuccess': 'Pregunta eliminada correctamente.',
    'questions.saveEdit': 'Guardar cambios',
    'questions.saveChanges': 'Guardar Cambios',
    'questions.saveQuestions': 'Guardar Preguntas',
    'questions.cancelEdit': 'Cancelar edición',
    'questions.confirmDelete': 'Confirmar Eliminación',
    'questions.deleteConfirmMessage': '¿Estás seguro de que deseas eliminar esta pregunta?',
    'questions.deleting': 'Eliminando...',
    'questions.regenerate': '🔄 Generar Preguntas',
    'questions.generateAnother': 'Generar otra pregunta',
    'workspace.intro': 'Revisa los candidatos que aplicaron a esta vacante y sus scores de idoneidad.',
    'workspace.compatibilityBreakdown': 'Desglose de compatibilidad',
    'workspace.candidateAnswers': 'Respuestas del candidato',
    'workspace.noCandidates': 'Aún no hay candidatos disponibles',
    'workspace.noCandidatesDesc': 'Los candidatos aparecerán aquí cuando apliquen a tu vacante.',
    'workspace.emptyHint': 'Comparte el enlace de la vacante para recibir postulaciones.',
    'workspace.deleteSuccess': 'Postulación eliminada.',
    'workspace.deleteError': 'Error al eliminar la postulación.',
    'workspace.deleteConfirmMessage': '¿Eliminar la postulación de {{name}}? Esta acción no se puede deshacer.',
    'workspace.confirmDelete': 'Confirmar Eliminación',
    'workspace.tooltipDelete': 'Eliminar postulación',
    'workspace.deleteWarning': 'Esta acción no se puede deshacer y eliminará todos los datos asociados al candidato en esta vacante.',
    'vacancy.saved': 'Vacante guardada correctamente',
    'login.welcome': 'Bienvenido',
    'login.subtitle': 'Plataforma de Reclutamiento IA',
    'login.continueGoogle': 'Continuar con Google',
    'login.connecting': 'Conectando...',
    'login.accessingPanel': 'Accediendo al Panel...',
    'login.errorServer': 'Error al procesar la respuesta del servidor.',
    'login.footer': '© AI Recruiter',
    'upload.title': 'Sube tu Descripción de Puesto',
    'upload.subtitle': 'Sube la descripción del puesto (PDF o Word) para comenzar',
    'upload.nextStep': 'Después podrás revisar los datos extraídos por la IA y publicar la vacante.',
    'upload.formatsLabel': 'Formatos aceptados:',
    'upload.clickInstruction': 'Haz clic en la esfera para seleccionar',
    'upload.dropHere': 'Arrastra y suelta el JD (PDF/DOCX) aquí',
    'upload.unsupportedFormat': 'Formato no válido. Usa un archivo PDF o DOCX.',
    'upload.uploading': 'Subiendo y analizando: "{{name}}". Esto tomará unos segundos...',
    'upload.analysisComplete': '¡Análisis de IA completo! Cargando editor...',
    'upload.stepUploading': 'Subiendo archivo...',
    'upload.stepExtracting': 'Extrayendo texto...',
    'upload.stepAnalyzing': 'Analizando con IA...',
    'upload.stepExtractInfo': 'Extrayendo información del JD',
    'upload.stepAnalyzingIA': 'Analizando con IA',
    'upload.stepSaving': 'Guardando resultados',
    'upload.timeoutMessage': 'El proceso está tomando más tiempo del esperado. Verifica en el panel si la vacante se creó correctamente.',
    'upload.analyzingDocument': 'Analizando documento',
    'upload.analyzingDocumentMessage': 'Estamos analizando el documento para extraer los datos de la vacante.',
    'upload.analyzingShort': 'Un momento...',
    'upload.extractionComplete': '¡Procesamiento exitoso!',
    'upload.extractionCompleteMessage': 'Hemos terminado de procesar el documento correctamente. Revisa los datos extraídos antes de publicar.',
    'upload.duplicateModalTitle': '¿Actualizar vacante existente?',
    'upload.duplicateModalMessage': 'Ya tienes una vacante con el mismo título: «{{title}}». ¿Quieres actualizar esa vacante con este JD o conservar la vacante nueva que acabamos de crear?',
    'upload.duplicateOverwrite': 'Actualizar esa vacante',
    'upload.duplicateKeepNew': 'Conservar la nueva',
    'upload.errorAnalysis': 'Error en el análisis de IA.',
    'upload.timeoutLong': 'El proceso está tomando más tiempo del esperado. Por favor, verifica en el panel de vacantes si la vacante se creó correctamente.',
    'upload.vacancyLimitReached': 'Ya tienes el máximo de vacantes en tu plan. Cambia una vacante a estado Cerrado o actualiza tu plan para subir otra.',
    'upload.gcsUnavailable': 'No se pudo guardar el archivo en el almacenamiento. Revisa la configuración de GCS en el servidor (credenciales y nombre del bucket).',
    'common.failed': 'Fallo',
    'common.unexpectedError': 'Error inesperado',
    'common.errorUpdate': 'Error al actualizar',
    // ──── Premium / Checkout ────
    'premium.title': 'Suscripción Premium',
    'premium.subtitle': 'Potencia tus procesos de reclutamiento con IA avanzada.',
    'premium.period': '/ mes',
    'premium.feature1': 'Análisis ilimitado de CVs',
    'premium.feature2': 'Entrevistas asistidas por IA',
    'premium.feature3': 'Reportes detallados de candidatos',
    'premium.feature4': 'Soporte prioritario',
    'premium.checkoutTitle': 'Finalizar Compra',
    'premium.cardInstructions': 'Introduce los datos de tu tarjeta',
    'premium.payNow': 'Pagar Ahora',
    'premium.paying': 'Procesando...',
    'premium.paid': '¡Pagado!',
    'premium.success': '¡Muchas gracias por tu compra!',
    'premium.errorInit': 'No se pudo inicializar el pago. Revisa la conexión.',
    'premium.errorPayment': 'Error en el pago: {{message}}',
    'premium.errorConfirm': 'No se pudo confirmar el pago con el servidor.',
    // ──── Layout / Sidebar ────
    'layout.expandMenu': 'Expandir menú',
    'layout.collapseMenu': 'Colapsar menú',
    'layout.themeDefault': 'Azul Clásico',
    'layout.themeViolet': 'Violeta',
    'layout.themeBlack': 'Negro puro',
    'layout.themeLightPure': 'Blanco puro',
    'layout.themeVioletInverted': 'Violeta invertido',
    'layout.notifications': 'Notificaciones',
    'layout.notificationsHeader': 'Notificaciones',
    'layout.markAllRead': 'Marcar todo como leído',
    'layout.noNotifications': 'No hay notificaciones',
    // ──── Settings – Notifications tab ────
    'settings.notifications.description': 'Resumen de notificaciones. Cuando haya una nueva aplicación a una vacante tuya, aparecerá aquí con el detalle.',
    'settings.notifications.markAllRead': 'Marcar todo como leído',
    'settings.notifications.none': 'No hay notificaciones.',
    'settings.notifications.vacancyLabel': 'Vacante: ',
    'settings.notifications.candidateLabel': 'Candidato: ',
    'settings.notifications.emailLabel': 'Email: ',
    'settings.profile.photoAlt': 'Foto de perfil',
    // ──── Onboarding ────
    'onboarding.step1Title': 'Bienvenido a AI Recruiter',
    'onboarding.step1Desc': 'Tu plataforma inteligente para gestionar el proceso de reclutamiento con IA. Comienza creando tu primera vacante.',
    'onboarding.step2Title': '1. Carga tu Job Description',
    'onboarding.step2Desc': 'Sube un archivo PDF o DOCX con la descripción del puesto. Nuestra IA extraerá automáticamente toda la información relevante.',
    'onboarding.step3Title': '2. Edita y Personaliza',
    'onboarding.step3Desc': 'Revisa y ajusta los datos extraídos por la IA. Puedes modificar habilidades, requisitos, salario y más detalles del puesto.',
    'onboarding.step4Title': '3. Configura las Preguntas',
    'onboarding.step4Desc': 'Personaliza las preguntas de la entrevista asistida por IA. Puedes agregar, editar o eliminar preguntas según tus necesidades.',
    'onboarding.step5Title': '4. Publica tu Vacante',
    'onboarding.step5Desc': 'Cambia el estado de tu vacante a ACTIVO para que los candidatos puedan encontrarla y aplicar. Obtendrás un enlace público para compartir.',
    'onboarding.step6Title': '5. Revisa Candidatos',
    'onboarding.step6Desc': 'En el Workspace podrás ver todos los candidatos que aplicaron, sus scores de idoneidad, análisis de IA y comparar perfiles fácilmente.',
    'onboarding.step7Title': '¡Listo para Empezar!',
    'onboarding.step7Desc': 'Ahora conoces las funcionalidades principales. Haz clic en el botón para crear tu primera vacante y comenzar a reclutar con IA.',
    'onboarding.skip': 'Omitir',
    'onboarding.previous': 'Anterior',
    'onboarding.next': 'Siguiente',
    'onboarding.start': 'Comenzar',
    // ──── Upload Circle ────
    'uploadCircle.defaultText': 'Arrastra tu JD aquí',
    'uploadCircle.successText': '¡Enviado!',
    // ──── Workspace ────
    'workspace.loading': 'Calculando Scores de Idoneidad...',
    'workspace.headerCandidate': 'Candidato',
    'workspace.headerStatus': 'Estado',
    'workspace.headerExp': 'Exp.',
    'workspace.headerSkills': 'Habilidades',
    'workspace.headerMatch': 'MATCH',
    'workspace.headerAction': 'Acción',
    'workspace.years': 'años',
    'workspace.contactLabel': 'Contacto:',
    'workspace.locationLabel': 'Ubicación:',
    'workspace.educationLabel': 'Educación:',
    'workspace.experienceLabel': 'Experiencia:',
    'workspace.certificationsLabel': 'Certificaciones:',
    'workspace.statusLabel': 'Estado:',
    'workspace.iaAnalysis': 'Análisis de IA',
    'workspace.experience': 'Experiencia',
    'workspace.technicalSkills': 'Habilidades Técnicas',
    'workspace.candidateResponses': 'Respuestas del Candidato',
    'workspace.clickToSeeAnswers': '(Haz clic para ver respuestas)',
    'workspace.softSkills': 'Habilidades Blandas',
    'workspace.education': 'Educación',
    'workspace.questionLabel': 'Pregunta {{num}}:',
    'workspace.answerLabel': 'Respuesta:',
    'workspace.noAnswers': 'No hay respuestas disponibles para este candidato.',
    'workspace.noBreakdown': 'El desglose de compatibilidad no está disponible para este candidato. Puede estar en proceso de cálculo.',
    'workspace.errorCouldNotDelete': 'No se pudo eliminar',
    'workspace.errorNetwork': 'Error de red',
    'workspace.errorLoadCandidates': 'No se pudo cargar la lista de candidatos.',
    'workspace.notScored': 'Sin evaluar',
    'workspace.estado.postulado': 'Postulado',
    'workspace.estado.en_revision': 'En revisión',
    'workspace.estado.entrevistado': 'Entrevistado',
    'workspace.estado.seleccionado': 'Seleccionado',
    'workspace.estado.descartado': 'Descartado',
    'workspace.errorEstado': 'No se pudo cambiar el estado del candidato.',
    'workspace.closeProcess': 'Cerrar proceso',
    'workspace.closeProcessHint': 'Cierra la vacante, contrata a quien tengas seleccionado y descarta al resto.',
    'workspace.closing': 'Cerrando...',
    'workspace.closeConfirmHired': 'Vas a contratar a {{name}} y a descartar a {{count}} candidato(s) que siguen en proceso. La vacante quedará cerrada. ¿Continuar?',
    'workspace.closeConfirmNoHire': 'No tienes a nadie seleccionado. Se descartará a {{count}} candidato(s) y la vacante quedará cerrada sin contratar. ¿Continuar?',
    'workspace.closeDone': 'Proceso cerrado. Se descartaron {{count}} candidatura(s).',
    'workspace.errorClose': 'No se pudo cerrar el proceso.',
    'workspace.notScoredHint': 'Se ha postulado, pero la IA todavía no lo ha evaluado.',
    // ──── Metrics ────
    'metrics.loading': 'Cargando métricas...',
    'metrics.errorLoading': 'Error al cargar métricas',
    'metrics.noDataTitle': 'Sin datos aún',
    'metrics.noDataDesc': 'Las métricas aparecerán cuando haya candidatos que apliquen.',
    'metrics.totalCandidates': 'Total Candidatos',
    'metrics.applicationsReceived': 'aplicaciones recibidas',
    'metrics.avgScore': 'Score Promedio',
    'metrics.avgMatch': 'idoneidad promedio',
    'metrics.topScore': 'Score Más Alto',
    'metrics.bestCandidate': 'mejor candidato',
    'metrics.viableCandidates': 'Candidatos Viables',
    'metrics.talentQuality': 'Calidad del Talento',
    'metrics.candidates': 'candidatos',
    'metrics.candidatesInRange': 'Candidatos en rango',
    'metrics.generalMatch': 'Match General',
    'metrics.weight': 'Peso',
    'metrics.candidateSource': 'Origen de Candidatos',
    'metrics.avgScoreLabel': 'Score Promedio',
    'metrics.experienceLevel': 'Experiencia',
    'metrics.keySkills': 'Habilidades Clave',
    'metrics.notEnoughData': 'Sin datos suficientes',
    'metrics.topScoreMentions': 'menciones en top scores',
    'metrics.frequencyNote': 'Frecuencia en candidatos con score > 75%',
    'metrics.educationTitle': 'Educación',
    'metrics.notSpecified': 'Sin especificar',
    // ──── Metrics – Radar categories ────
    'metrics.radar.experience': 'Experiencia',
    'metrics.radar.technicalSkills': 'Hab. Técnicas',
    'metrics.radar.responses': 'Respuestas',
    'metrics.radar.softSkills': 'Hab. Blandas',
    'metrics.radar.education': 'Educación',
    // ──── Metrics – Education labels ────
    'metrics.edu.bachelors': 'Licenciatura',
    'metrics.edu.masters': 'Maestría',
    'metrics.edu.doctorate': 'Doctorado',
    'metrics.edu.certifications': 'Certificaciones',
    'metrics.edu.other': 'Otros',
    // ──── Candidate Flow ────
    'candidate.default': 'Candidato',
    'candidate.loading': 'Cargando...',
    'candidate.expandMenu': 'Expandir menú',
    'candidate.collapseMenu': 'Colapsar menú',
    'layout.changeLanguage': 'Cambiar Idioma',
    'candidate.switchToEnglish': 'Switch to English',
    'candidate.switchToSpanish': 'Cambiar a Español',
    'candidate.logout': 'Cerrar sesión',
    // Step 1 – CV Upload
    'candidate.step1.title': 'Sube tu CV',
    'candidate.step1.description': 'Arrastra tu archivo CV aquí o haz clic para seleccionarlo',
    'candidate.step1.formats': 'Formatos aceptados:',
    'candidate.step1.dragHere': 'Arrastra tu CV aquí',
    'candidate.step1.cvProcessed': 'CV procesado',
    'candidate.step1.stepsTitle': 'Pasos del Proceso',
    'candidate.step1.stepsDescription': 'Completa los siguientes requerimientos para postularte a esta vacante:',
    'candidate.step1.uploadCV': 'Subir tu CV actualizado',
    'candidate.step1.verifyInfo': 'Verificar información extraída',
    'candidate.step1.recordVideo': 'Graba un video de presentación',
    'candidate.step1.answerQuestions': 'Responde preguntas de la vacante',
    'candidate.step1.linkCalendar': 'Vincular tu calendario de Google',
    'candidate.step1.scheduleInterview': 'Agendar día y hora de entrevista',
    // Step Review Data
    'candidate.review.analyzingTitle': 'Analizando Perfil...',
    'candidate.review.analyzingSubtitle': 'Estamos organizando la información extraída de tu CV.',
    'candidate.review.title': 'Validación de Perfil',
    'candidate.review.subtitle': 'Revisa que la información extraída sea correcta. Haz clic en cualquier campo para editarlo.',
    'candidate.review.personalInfo': 'Información Personal',
    'candidate.review.fullName': 'Nombre Completo',
    'candidate.review.fullNamePlaceholder': 'Tu nombre',
    'candidate.review.email': 'Email Profesional',
    'candidate.review.phone': 'Teléfono de Contacto',
    'candidate.review.profileSummary': 'Resumen de Perfil',
    'candidate.review.profileSummaryPlaceholder': 'Breve descripción de tu trayectoria...',
    'candidate.review.vacancySource': '¿Cómo te enteraste de esta vacante?',
    'candidate.review.selectOption': 'Selecciona una opción',
    'candidate.review.referred': 'Referido / Conocido',
    'candidate.review.jobBoard': 'Bolsa de Trabajo (OCC, CompuTrabajo, etc.)',
    'candidate.review.companyWebsite': 'Sitio Web de la empresa',
    'candidate.review.socialNetworks': 'Redes Sociales (Facebook, Instagram, X)',
    'candidate.review.university': 'Universidad / Escuela',
    'candidate.review.other': 'Otro',
    'candidate.review.workExperience': 'Experiencia Laboral',
    'candidate.review.jobTitle': 'Cargo / Posición',
    'candidate.review.company': 'Empresa',
    'candidate.review.period': 'Periodo',
    'candidate.review.start': 'Inicio',
    'candidate.review.end': 'Fin',
    'candidate.review.location': 'Ubicación',
    'candidate.review.responsibilities': 'Responsabilidades y Logros',
    'candidate.review.responsibilitiesPlaceholder': 'Una responsabilidad por línea',
    'candidate.review.education': 'Educación',
    'candidate.review.degree': 'Título / Grado Académico',
    'candidate.review.institution': 'Institución Universitaria / Escuela',
    'candidate.review.projects': 'Proyectos Destacados',
    'candidate.review.projectName': 'Nombre del Proyecto',
    'candidate.review.technologies': 'Tecnologías Utilizadas',
    'candidate.review.projectDescription': 'Descripción del Proyecto',
    'candidate.review.skills': 'Habilidades',
    'candidate.review.languages': 'Idiomas',
    'candidate.review.certifications': 'Certificaciones',
    'candidate.review.certName': 'Nombre de la Certificación',
    'candidate.review.certIssuer': 'Entidad Emisora',
    'candidate.review.saving': '⏳ Guardando...',
    'candidate.review.confirmed': '✓ ¡Datos Confirmados!',
    'candidate.review.confirmData': 'Confirmar Datos',
    // Step 2 – Video Recorder
    'candidate.video.prepTitle': 'Prepárate para tu video presentación',
    'candidate.video.prepSubtitle': 'Explica brevemente tu experiencia y por qué te interesa esta posición',
    'candidate.video.lighting': 'Iluminación',
    'candidate.video.lightingTip': 'Asegúrate de estar en un lugar bien iluminado, preferiblemente con luz de frente.',
    'candidate.video.sound': 'Sonido',
    'candidate.video.soundTip': 'Busca un entorno silencioso para que podamos escucharte con claridad.',
    'candidate.video.framing': 'Encuadre',
    'candidate.video.framingTip': 'Coloca la cámara a la altura de tus ojos y mantén una distancia cómoda.',
    'candidate.video.naturalness': 'Naturalidad',
    'candidate.video.naturalnessTip': 'No te preocupes por ser perfecto, sé tú mismo. Estamos emocionados de escucharte.',
    'candidate.video.start': 'Comenzar',
    'candidate.video.recordingTitle': 'Grabación de Entrevista',
    'candidate.video.cameraPreview': 'Previsualización de la cámara',
    'candidate.video.startRecording': 'Iniciar Grabación',
    'candidate.video.stopRecording': 'Detener Grabación',
    'candidate.video.uploadRecorded': 'Subir Video Grabado',
    'candidate.video.downloadVideo': 'Descargar Video',
    'candidate.video.uploadSuccess': '¡Video subido con éxito!',
    'candidate.video.uploading': 'Subiendo video...',
    'candidate.video.or': 'o',
    'candidate.video.uploadOwn': 'Sube tu Propio Video',
    'candidate.video.selectVideo': 'Seleccionar Video',
    'candidate.video.selectedFile': 'Archivo seleccionado: {{name}}',
    'candidate.video.uploadFile': 'Subir Archivo',
    // Step 3 – Questions
    'candidate.questions.title': 'Entrevista Asistida por IA',
    'candidate.questions.selectVacancy': 'Selecciona la vacante a la que deseas aplicar:',
    'candidate.questions.loadingVacancies': 'Cargando vacantes disponibles...',
    'candidate.questions.noVacancies': 'No se encontraron vacantes disponibles.',
    'candidate.questions.selectThis': 'Seleccionar Esta Vacante',
    'candidate.questions.yearsExp': '{{years}}+ años',
    'candidate.questions.answerPlaceholder': 'Escribe tu respuesta aquí...',
    'candidate.questions.charCount': '{{count}} / {{max}} caracteres',
    'candidate.questions.shortAnswerHint': 'Una respuesta más detallada se evalúa mejor.',
    'candidate.questions.preparingInterview': 'Preparando tu entrevista personalizada...',
    'candidate.questions.answerInstructions': 'Responde a las siguientes preguntas para completar tu entrevista:',
    'candidate.questions.changeVacancy': 'Cambiar Vacante',
    'candidate.questions.salaryLabel': '¿Cuál es tu expectativa salarial neta mensual?',
    'candidate.questions.salaryPlaceholder': 'Ej: $25,000 netos mensuales',
    'candidate.questions.salaryHint': 'Indica el monto neto mensual en pesos mexicanos (MXN)',
    'candidate.questions.submit': 'Enviar Respuestas',
    'candidate.questions.incompleteAlert': 'Contesta todas las preguntas para continuar.',
    'candidate.questions.errorSave': 'Error al guardar respuestas: {{error}}',
    'candidate.questions.noQuestions': 'No se pudieron obtener las preguntas para esta vacante.',
    'candidate.questions.pendingReview': 'Las preguntas de esta vacante están siendo revisadas por el reclutador. Por favor, intenta más tarde.',
    'candidate.questions.rejected': 'Las preguntas de esta vacante fueron rechazadas. El reclutador debe generar nuevas preguntas.',
    'candidate.questions.checkConnection': 'Verifica la conexión o intenta de nuevo.',
    'candidate.questions.tryAgain': 'Intentar De Nuevo',
    // Step 4 – Calendar
    'candidate.calendar.verifying': 'Verificando acceso al calendario...',
    'candidate.calendar.processing': 'Procesando Conexión...',
    'candidate.calendar.processingMessage': 'Estamos confirmando la conexión con Google. Por favor, espera un momento, esto puede tardar unos segundos.',
    'candidate.calendar.connectTitle': 'Conecta tu Calendario para Entrevistas',
    'candidate.calendar.connectMessage': 'Conecta tu Google Calendar para que podamos ver tu disponibilidad y enviarte invitaciones de entrevista.',
    'candidate.calendar.connectButton': 'Conectar Google Calendar',
    'candidate.calendar.loadingUser': 'Cargando Usuario...',
    'candidate.calendar.waitingAuth': 'Esperando la autenticación de Firebase para obtener tu ID de usuario...',
    'candidate.calendar.errorConnect': 'Error al conectar con Google Calendar. Por favor, inténtalo de nuevo.',
    'candidate.calendar.errorRetry': 'Error: {{error}}. Por favor, inténtalo de nuevo.',
    // Step 5 – Calendar Success
    'candidate.calendarSuccess.title': 'Calendario Conectado Exitosamente',
    'candidate.calendarSuccess.message': 'Gracias por conectar tu Google Calendar. Ya podemos verificar tu disponibilidad para futuras entrevistas.',
    'candidate.calendarSuccess.errorTitle': 'Falló la Conexión del Calendario',
    'candidate.calendarSuccess.errorDetail': 'Detalle del Error:',
    'candidate.calendarSuccess.errorHint': 'Por favor, inténtalo de nuevo. Asegúrate de otorgar todos los permisos solicitados por Google.',
    'candidate.calendarSuccess.defaultError': 'Ocurrió un error desconocido durante la conexión.',
    'candidate.calendarSuccess.back': 'Volver al inicio',
    // Step 6 – Schedule Interview
    'candidate.schedule.title': 'Agendar Entrevista',
    'candidate.schedule.description': 'Selecciona un día y horario disponible para tu entrevista.',
    'candidate.schedule.queryingTimes': 'Consultando horarios disponibles...',
    'candidate.schedule.notice': 'Aviso:',
    'candidate.schedule.reconnect': 'Re-conectar Google Calendar',
    'candidate.schedule.fallbackHint': 'Puedes elegir día y hora a continuación.',
    'candidate.schedule.selectDay': 'Selecciona un día:',
    'candidate.schedule.availableTimes': 'Horarios disponibles:',
    'candidate.schedule.noSlots': 'No hay horarios disponibles para este día.',
    'candidate.schedule.selected': 'Has seleccionado: {{date}} a las {{time}}',
    'candidate.schedule.confirm': 'Confirmar Entrevista',
    'candidate.schedule.scheduling': 'Agendando...',
    'candidate.schedule.processingTitle': 'Procesando tu cita...',
    'candidate.schedule.processingMessage': 'Estamos agendando tu entrevista y enviando las invitaciones correspondientes.',
    'candidate.schedule.completedTitle': '¡Aplicación Finalizada!',
    'candidate.schedule.completedMessage': 'Has completado todos los pasos de tu postulación con éxito.',
    'candidate.schedule.proposedTime': 'Horario Propuesto para Entrevista',
    'candidate.schedule.pendingValidation': 'El reclutador revisará tu perfil y, en caso de ser viable, se confirmará la entrevista vía Google Meet. Recibirás una notificación por correo electrónico. La cita puede estar sujeta a cambios de acuerdo a la disponibilidad del reclutador.',
    'candidate.schedule.thanks': '¡Gracias por aplicar! Te deseamos mucho éxito.',
    'candidate.schedule.applyOther': 'Aplicar a otra vacante',
    'candidate.schedule.finishProcess': 'Finalizar proceso',
    'candidate.schedule.exploreOther': 'Explora otras vacantes',
    'candidate.schedule.exploreMessage': 'Aún puedes aplicar a otras posiciones que coincidan con tu perfil:',
    'candidate.schedule.backConfirmation': 'Volver a mi confirmación',
    'candidate.schedule.authError': 'Error de autenticación. Por favor, reinicia la sesión.',
    'candidate.schedule.selectTime': 'Por favor, selecciona un horario.',
    'candidate.schedule.tokenNotFound': 'Token de sesión no encontrado.',
    'candidate.schedule.errorSchedule': 'Error al agendar la entrevista',
    'candidate.schedule.errorScheduleAlert': 'Error al agendar entrevista: {{error}}',
    'candidate.schedule.mustAuth': 'Debes estar autenticado para conectar tu calendario.',
    'candidate.schedule.errorQueryAvailability': 'Fallo al consultar disponibilidad:',
    'candidate.schedule.errorFetchAvailability': 'Error al obtener disponibilidad.',
    // Stepper
    'candidate.stepper.verifyingSession': 'Verificando sesión...',
    'candidate.stepper.loadingSession': 'Cargando sesión de usuario...',
    'candidate.stepper.processTitle': 'Proceso de Aplicación',
    'candidate.stepper.processDescription': 'Completa cada uno de estos 5 pasos para potenciar tu postulación profesional',
    'candidate.stepper.startProcess': 'Comenzar Proceso',
    'candidate.stepper.previous': 'Anterior',
    'candidate.stepper.previousStep': 'Paso anterior',
    'candidate.stepper.reviewLabel': 'Revisión',
    'candidate.stepper.interviewLabel': 'Entrevista',
    'candidate.stepper.scheduleLabel': 'Agendar',
    'candidate.stepper.errorAuth': 'Error: Usuario no autenticado',
    'candidate.stepper.errorUploadCV': 'Error al subir el CV: {{error}}',
    'candidate.stepper.errorExtractCV': 'Tu CV se guardó, pero no pudimos leerlo automáticamente: {{error}}. Inténtalo de nuevo en unos minutos.',
    'candidate.stepper.errorExtractCVUnknown': 'el servicio de lectura no está disponible',
    'candidate.stepper.alreadyApplied': 'Ya te postulaste a esta vacante el {{date}}. Si continúas, reemplazarás tu CV, tu video y tus respuestas anteriores. ¿Quieres continuar?',
    'candidate.stepper.alreadyResolved': 'Esta vacante ya se resolvió y tu candidatura está como "{{estado}}". Ya no se pueden enviar cambios.',
    'candidate.stepper.previousInProgress': 'Ya te postulaste a esta vacante el {{date}}. Estado: {{estado}}. Si vuelves a enviar, reemplazarás lo anterior.',
    'candidate.stepper.previousResolved': 'Esta vacante ya se resolvió. Tu candidatura quedó como: {{estado}}.',
    'candidate.stepper.seeOtherVacancies': 'Ver otras vacantes',
    'candidate.stepper.errorUploadVideo': 'Error al subir el video: {{error}}',
    'candidate.stepper.errorConfirmData': 'Error al confirmar datos: {{error}}',
    'candidate.stepper.processingSuccess': '¡Procesamiento Exitoso!',
    'candidate.stepper.processingSuccessMessage': 'Hemos terminado de procesar tu información correctamente.',
    'candidate.stepper.confirmingProfile': 'Confirmando Perfil',
    'candidate.stepper.confirmingProfileMessage': 'Validando tus datos profesionales para el siguiente paso.',
    'candidate.stepper.sendingAnswers': 'Enviando Respuestas',
    'candidate.stepper.sendingAnswersMessage': 'Estamos procesando tu entrevista para el reclutador.',
    'candidate.stepper.processingCV': 'Procesando tu CV',
    'candidate.stepper.processingCVMessage': 'Estamos analizando tu trayectoria para agilizar tu postulación profesional.',
    'candidate.stepper.processingVideo': 'Procesando tu Video',
    'candidate.stepper.processingVideoMessage': 'Estamos analizando tu video para evaluar tus habilidades y competencias.',
    'candidate.stepper.cvStep1': 'Subiendo CV a nuestros servidores',
    'candidate.stepper.cvStep2': 'Extrayendo información profesional con IA',
    'candidate.stepper.cvStep3': 'Estructurando perfil de candidato',
    'candidate.stepper.videoStep1': 'Subiendo video',
    'candidate.stepper.videoStep2': 'Analizando sentimientos y emociones',
    'candidate.stepper.videoStep3': 'Evaluando comunicación verbal y no verbal',
    'candidate.stepper.videoStep4': 'Calculando scoring y guardando resultados',
    // Saite Companion
    'candidate.saite.step1': '¡Hola! Soy Saity. Sube tu CV para empezar, ¡estaré aquí para guiarte!',
    'candidate.saite.step2': '¡Listo! Revisemos que tu información esté perfecta antes de seguir.',
    'candidate.saite.step3': '¡Qué buen perfil! Ahora prepárate, vamos a grabar una breve video-entrevista.',
    'candidate.saite.step4': '¡Excelente video! Ahora unas preguntas rápidas para conocerte mejor.',
    'candidate.saite.step5': '¡Casi lo tienes! Conecta tu calendario para agendar nuestra plática.',
    'candidate.saite.step6': '¡Lo lograste! Elige el horario que más te convenga para la entrevista.',
    'candidate.review.addWorkExp': '+ Agregar Experiencia',
    'candidate.review.noWorkExp': 'No se detectó experiencia laboral en tu CV. Puedes agregarla manualmente.',
    'candidate.review.removeExperience': 'Eliminar esta experiencia',
    'candidate.review.remove': 'Eliminar',
    'candidate.review.addEducation': '+ Agregar Educación',
    'candidate.review.noEducation': 'No se detectó formación académica en tu CV. Puedes agregarla manualmente.',
    'candidate.review.removeEducation': 'Eliminar esta educación',
    'candidate.review.details': 'Detalles adicionales',
    'candidate.review.add': '+ Agregar',
    'candidate.review.noSkills': 'No se detectaron habilidades. Agrégalas manualmente.',
    'candidate.review.selectCategory': 'Selecciona una categoría',
    'candidate.review.skillsPlaceholder': 'Habilidades separadas por coma',
    'candidate.review.noLanguages': 'No se detectaron idiomas. Agrégalos manualmente.',
    'candidate.review.languagePlaceholder': 'Idioma (ej: Inglés)',
    'candidate.review.levelPlaceholder': 'Nivel (ej: B2, Avanzado)',
    'candidate.review.addProject': '+ Agregar Proyecto',
    'candidate.review.noProjects': 'No se detectaron proyectos. Si tienes proyectos relevantes, agrégalos aquí.',
    'candidate.review.removeProject': 'Eliminar este proyecto',
    'candidate.review.technologiesPlaceholder': 'React, Python, PostgreSQL...',
    'candidate.review.technologiesHint': '(separadas por coma)',
    'candidate.review.projectDescriptionPlaceholder': '¿En qué consistió el proyecto? ¿Cuál fue tu rol?',
    'candidate.review.addCertification': '+ Agregar Certificación',
    'candidate.review.noCertifications': 'No se detectaron certificaciones. Si tienes certificados relevantes, agrégalos aquí.',
    'candidate.review.removeCertification': 'Eliminar esta certificación',
    'candidate.review.issueDate': 'Fecha de Emisión',
    'candidate.review.credentialId': 'ID de Credencial',
    'candidate.review.credentialPlaceholder': 'ID o código de verificación',
    'candidate.review.confirmSuccess': '✓ ¡Datos Confirmados!',
    'candidate.review.confirmButton': 'Confirmar Datos',
    'candidate.review.link': 'Enlace / URL',
    'candidate.review.certNamePlaceholder': 'Ej: AWS Certified Developer',
    'candidate.review.certIssuerPlaceholder': 'Ej: Amazon Web Services',
    'candidate.review.educationPlaceholder': 'Ej: Ingeniería en Sistemas',
    'candidate.review.institutionPlaceholder': 'Nombre de la institución',
    'candidate.review.detailsPlaceholder': 'Ej: Promedio 9.2, Titulado con Mención Honorífica',
    'candidate.review.jobTitlePlaceholder': 'Ej: Desarrollador Senior',
    'candidate.review.companyPlaceholder': 'Nombre de la empresa',
    'candidate.review.locationPlaceholder': 'Ciudad, País',
    'candidate.review.city': 'Ciudad',
    'candidate.review.state': 'Estado / Provincia',
    'candidate.review.country': 'País',
    'candidate.review.statePlaceholder': 'Estado o provincia',
    'candidate.review.skillCategory.tecnicas': 'Técnicas',
    'candidate.review.skillCategory.blandas': 'Blandas',
    'candidate.review.skillCategory.herramientas': 'Herramientas',
    'candidate.review.skillCategory.programacion': 'Lenguajes de Programación',
    'candidate.review.skillCategory.frameworks': 'Frameworks y Librerías',
    'candidate.review.skillCategory.db': 'Bases de Datos',
    'candidate.review.skillCategory.devops': 'Cloud y DevOps',
    'candidate.review.skillCategory.design': 'Diseño',
    'candidate.review.skillCategory.management': 'Gestión de Proyectos',
    'candidate.review.skillCategory.others': 'Otras',
    'candidate.review.source.referred': 'Referido / Conocido',
    'candidate.review.source.jobBoard': 'Bolsa de Trabajo (OCC, CompuTrabajo, etc.)',
    'candidate.review.source.companyWebsite': 'Sitio Web de la empresa',
    'candidate.review.source.socialNetworks': 'Redes Sociales (Facebook, Instagram, X)',
    'candidate.review.source.university': 'Universidad / Escuela',
    'candidate.review.source.other': 'Otro',
    'candidate.video.fileSizeError': 'El archivo seleccionado pesa {{size}} MB y supera el límite de {{max}} MB. Por favor selecciona un video más pequeño.',
    'candidate.calendar.userNotAuth': 'El usuario no está autenticado o está cargando.',
    'candidate.calendar.tokenNotFound': 'Token de sesión no encontrado.',
    'candidate.questions.errorFetch': 'No se pudieron obtener las preguntas para esta vacante.',
    'candidate.questions.errorLoad': 'Error al cargar las preguntas. Por favor, intenta de nuevo.',
    'shared.profilePhotoAlt': 'Foto de perfil',
  },
  en: {
    'sidebar.panel': 'Vacancies Panel',
    'sidebar.new': 'Load Vacancy',
    'sidebar.premium': 'Premium',
    'sidebar.settings': 'Settings',
    'sidebar.backToAdmin': 'Admin panel',
    'sidebar.account': 'Account',
    'sidebar.logout': 'Log out',
    'sidebar.closing': 'Logging out...',
    'privacy.trigger': 'Privacy Notice',
    'privacy.modalTitle': 'Privacy Notice',
    'privacy.modalSubtitle': 'AI Recruitment Platform',
    'privacy.closeLabel': 'Close privacy notice',
    'privacy.section1Title': 'Data Controller',
    'privacy.section1Text': 'AI Recruiter is the entity responsible for processing the personal data you provide during your recruitment process through this platform.',
    'privacy.section2Title': 'Data We Collect',
    'privacy.section2Intro': 'The following personal data is collected during the application process:',
    'privacy.section2Item1': 'Full name and email address (through your Google account)',
    'privacy.section2Item2': 'Profile photo from your Google account',
    'privacy.section2Item3': 'Curriculum Vitae (CV) in PDF or DOCX format',
    'privacy.section2Item4': 'Information extracted from your CV using Artificial Intelligence (work experience, education, skills, etc.)',
    'privacy.section2Item5': 'Personal introduction video',
    'privacy.section2Item6': 'Interview question responses',
    'privacy.section2Item7': 'Availability and Google Calendar schedule information',
    'privacy.section3Title': 'Purpose of Processing',
    'privacy.section3Intro': 'Your personal data will be used exclusively for:',
    'privacy.section3Item1': 'Evaluating your professional profile for the position you are applying to',
    'privacy.section3Item2': 'Processing and analyzing your CV using Artificial Intelligence tools',
    'privacy.section3Item3': 'Generating a compatibility score with the job profile',
    'privacy.section3Item4': 'Facilitating communication between you and the recruiter',
    'privacy.section3Item5': 'Scheduling interviews through Google Calendar',
    'privacy.section4Title': 'Use of Artificial Intelligence',
    'privacy.section4Text': 'This platform uses Artificial Intelligence models (Google Gemini) to analyze your CV, generate personalized interview questions, and evaluate your responses. Information processed by AI is used solely as a support tool in the selection process, and final hiring decisions are always made by people.',
    'privacy.section5Title': 'Data Transfer',
    'privacy.section5Text': 'Your data may be shared with third-party services necessary for the platform to function: Google (authentication, calendar, and AI), Firebase (secure storage), and Stripe (recruiter payment processing). We will not sell or share your personal information with third parties for commercial purposes.',
    'privacy.section6Title': 'Data Retention',
    'privacy.section6Text': 'Your personal data will be retained for the time necessary to complete the recruitment process for the position you applied to. You may request the deletion of your data at any time by contacting us directly.',
    'privacy.section7Title': 'Your Rights (ARCO)',
    'privacy.section7Intro': 'As the owner of your personal data, you have the right to:',
    'privacy.section7Access': 'Access:',
    'privacy.section7AccessDesc': ' Know what data we have about you',
    'privacy.section7Rectification': 'Rectification:',
    'privacy.section7RectificationDesc': ' Correct inaccurate or incomplete data',
    'privacy.section7Cancellation': 'Cancellation:',
    'privacy.section7CancellationDesc': ' Request the deletion of your data',
    'privacy.section7Opposition': 'Opposition:',
    'privacy.section7OppositionDesc': ' Object to the processing of your data',
    'privacy.section7Contact': 'To exercise these rights, you may contact us through the email address provided by the company responsible for the recruitment process.',
    'privacy.section8Title': 'Security',
    'privacy.section8Text': 'Your personal data is stored securely using Google Firebase, with encryption in transit and at rest. We implement administrative, technical, and physical security measures to protect your information against unauthorized access, loss, or alteration.',
    'privacy.section9Title': 'Consent',
    'privacy.section9Text': 'By continuing with the application process and uploading your CV, video, or answering the questions, you accept the terms of this privacy notice and consent to the processing of your personal data as described herein.',
    'privacy.lastUpdate': 'Last updated: February 2026',
    'settings.tab.profile': 'Profile',
    'settings.tab.notifications': 'Notifications',
    'settings.tab.privacy': 'Privacy',
    'settings.tab.theme': 'Theme',
    'settings.tab.language': 'Language',
    'settings.loading': 'Loading settings...',
    'settings.profile.noName': 'No name',
    'settings.profile.noEmail': 'No email',
    'settings.notifications.soon': 'Email notification options (coming soon).',
    'settings.privacy.soon': 'Manage your privacy and data (coming soon).',
    'settings.theme.title': 'Appearance',
    'settings.theme.default': 'Default (blue)',
    'settings.theme.violet': 'Violet',
    'settings.theme.lightPure': 'Pure white',
    'settings.theme.violetInverted': 'Violet inverted',
    'settings.theme.black': 'Pure Black',
    'settings.language.title': 'Language',
    'settings.language.es': 'Spanish',
    'settings.language.en': 'English',
    'title.panel': 'Vacancies Panel',
    'title.new': 'Load Vacancy',
    'title.checkout': 'Premium',
    'title.settings': 'Settings',
    'title.edit': 'Edit vacancy',
    'title.workspace': 'Workspace',
    'title.candidatesList': 'Candidates list',
    'title.questions': 'Vacancy questions',
    'title.metrics': 'Metrics',
    'nav.backToEdit': 'Back to edit vacancy',
    'nav.backToPanel': 'Back to panel',
    'dashboard.intro': 'Manage your vacancies. Filter, change status, and access workspace or questions from here.',
    'dashboard.loading': 'Loading vacancies...',
    'dashboard.retry': 'Retry',
    'dashboard.noResultsHint': 'No vacancies match the current filters.',
    'dashboard.titleColumn': 'Title',
    'dashboard.filterSearch': 'Search by title...',
    'dashboard.statusColumn': 'Status',
    'dashboard.filterAll': 'All',
    'dashboard.createdAt': 'Created',
    'dashboard.filterAllDates': 'No order',
    'dashboard.filterDateOldest': 'Oldest first',
    'dashboard.filterDateNewest': 'Newest first',
    'dashboard.updatedAt': 'Last updated',
    'dashboard.actions': 'Actions',
    'dashboard.errorUpdate': 'Could not update',
    'dashboard.deleteSuccess': 'Vacancy "{{title}}" deleted successfully.',
    'dashboard.errorDelete': 'Could not delete vacancy. Please try again.',
    'dashboard.tooltipWorkspace': 'View candidates for this vacancy',
    'dashboard.tooltipMetrics': 'View vacancy metrics',
    'dashboard.tooltipCopyUrl': 'Copy public URL',
    'dashboard.linkCopied': 'Link copied',
    'dashboard.tooltipDelete': 'Delete vacancy',
    'dashboard.tooltipQuestions': 'Edit interview questions',
    'dashboard.tooltipEdit': 'Edit vacancy',
    'dashboard.tooltipStatus': 'Change vacancy status',
    'dashboard.tooltipFilterSearch': 'Search by job title',
    'dashboard.tooltipFilterStatus': 'Filter by status',
    'dashboard.tooltipFilterDate': 'Sort by date',
    'dashboard.noTitle': 'No Title',
    // ──── Admin ────
    'admin.redirecting': 'Redirecting...',
    'admin.account': 'Account',
    'admin.loading': 'Loading...',
    'admin.dashboard': 'Dashboard',
    'admin.users': 'Users',
    'admin.vacancies': 'Vacancies',
    'admin.config': 'Configuration',
    'admin.audit': 'Audit',
    'admin.aiUsage': 'AI Usage',
    'admin.notifications': 'Notifications',
    'admin.markAllRead': 'Mark all as read',
    'admin.noNotifications': 'No notifications',
    'admin.breadcrumb': 'Administration Panel',
    'admin.sidebar.expand': 'Expand menu',
    'admin.sidebar.collapse': 'Collapse menu',
    'admin.logout': 'Log out',
    'admin.dashboard.title': 'Administrative Dashboard',
    'admin.dashboard.cardCandidates': 'Candidates',
    'admin.dashboard.cardVacancies': 'Vacancies',
    'admin.dashboard.cardRecruiters': 'Recruiters',
    'admin.dashboard.cardApplications': 'Applications',
    'admin.dashboard.suspendedAlert': 'Attention! There are {{count}} suspended users requiring review.',
    'notification.welcomeAdminTitle': 'Admin panel',
    'notification.welcomeAdminMessage': 'Here you will see platform notifications (new applications, etc.).',
    'notification.welcomeRecruiterTitle': 'Welcome to AI Recruiter',
    'notification.welcomeRecruiterMessage': 'Saity will help you throughout your process with AI.',
    'admin.dashboard.weeklyTrends': 'Weekly Trends',
    'admin.dashboard.colWeek': 'Week',
    'admin.dashboard.colCandidates': 'Candidates',
    'admin.dashboard.colVacancies': 'Vacancies',
    'admin.dashboard.colApplications': 'Applications',
    'admin.users.title': 'User Management',
    'admin.users.filterEmail': 'Email',
    'admin.users.filterUid': 'UID',
    'admin.users.filterAllRoles': 'All roles',
    'admin.users.roleCandidate': 'Candidate',
    'admin.users.roleRecruiter': 'Recruiter',
    'admin.users.roleAdmin': 'Admin',
    'admin.users.filterAllStatuses': 'All statuses',
    'admin.users.statusActive': 'Active',
    'admin.users.statusSuspended': 'Suspended',
    'admin.users.search': 'Search',
    'admin.users.columnRole': 'Role',
    'admin.users.columnEmail': 'Email',
    'admin.users.columnName': 'Name',
    'admin.users.columnStatus': 'Status',
    'admin.users.columnActions': 'Actions',
    'admin.users.activate': 'Activate',
    'admin.users.suspend': 'Suspend',
    'admin.users.impersonate': 'Impersonate',
    'admin.users.impersonateTitle': 'Log in as this user',
    'admin.users.changeRole': 'Change role',
    'admin.users.rolePending': 'Pending assignment',
    'admin.users.ownerTitle': 'Instance owner. The only one who can appoint or remove administrators. Transfer ownership before changing their role.',
    'admin.users.transfer': 'Make owner',
    'admin.users.transferTitle': 'Transfer instance ownership to this person',
    'admin.users.transferConfirm': 'Transfer instance ownership to {email}? You will stop being the owner, but remain an administrator.',
    'pending.title': 'Your account is pending approval',
    'pending.lead': 'You signed in successfully, but no role has been assigned to you yet.',
    'pending.hint': 'Ask an administrator to assign you a role. Once they do, sign in again for it to take effect.',
    'pending.signOut': 'Sign out',
    'admin.users.roleLockedTitle': 'Admin via the deployment ADMIN_EMAILS variable. Edit that variable to change it.',
    'admin.impersonation.label': 'Viewing as user: {{uid}}',
    'admin.impersonation.exit': 'Exit',
    'admin.config.title': 'System Configuration',
    'admin.config.tabGeneral': 'General',
    'admin.config.tabNotifications': 'Notifications',
    'admin.config.featureFlags': 'Feature flags',
    'admin.config.maintenanceMode': 'Maintenance mode',
    'admin.config.newCandidateFlow': 'New candidate flow',
    'admin.config.limitsFree': 'Limits (free plan)',
    'admin.config.limitVacancies': 'Max vacancies per recruiter (free)',
    'admin.config.generalKeyValue': 'General (key-value)',
    'admin.config.noKeys': 'No other keys. Add one below if needed.',
    'admin.config.newKeyPlaceholder': 'New key',
    'admin.config.newValuePlaceholder': 'Value (text or JSON)',
    'admin.config.add': 'Add',
    'admin.config.saving': 'Saving...',
    'admin.config.save': 'Save',
    'admin.config.savedMsg': 'Configuration saved.',
    'admin.config.notifPlatformTitle': 'Platform Notifications',
    'admin.config.notifDescription': 'Summary of notifications (new applications, system notices). The header bell shows the summary; here you can see the full detail.',
    'admin.config.deleteAll': 'Delete all',
    'admin.config.deleteConfirm': 'Delete all notifications?',
    'admin.config.markRead': 'Mark read',
    'admin.config.labelVacancy': 'Vacancy',
    'admin.config.labelIdVacancy': 'Vacancy ID',
    'admin.config.labelCandidate': 'Candidate',
    'admin.config.labelEmail': 'Email',
    'admin.config.labelIdApp': 'App ID',
    'admin.config.goToPanel': 'Go to panel',
    'admin.audit.title': 'Audit Log',
    'admin.audit.filterAdminEmail': 'Admin (email)',
    'admin.audit.filterAllActions': 'All actions',
    'admin.audit.filterFrom': 'From',
    'admin.audit.filterTo': 'To',
    'admin.audit.search': 'Search',
    'admin.audit.exportCsv': 'Export CSV',
    'admin.audit.colDate': 'Date',
    'admin.audit.colAdmin': 'Admin',
    'admin.audit.colAction': 'Action',
    'admin.audit.colTarget': 'Target',
    'admin.audit.colIp': 'IP',
    'admin.audit.prev': 'Previous',
    'admin.audit.next': 'Next',
    'admin.aiMonitoring.title': 'AI Monitoring',
    'admin.aiMonitoring.summary': 'Log of calls to Gemini (tokens per call). This page: {{total}} tokens.',
    'admin.aiMonitoring.colDate': 'Date',
    'admin.aiMonitoring.colModel': 'Model',
    'admin.aiMonitoring.colCaller': 'Caller',
    'admin.aiMonitoring.colInput': 'Input',
    'admin.aiMonitoring.colOutput': 'Output',
    'admin.aiMonitoring.colTotal': 'Total',
    'admin.aiMonitoring.colError': 'Error',
    'editor.hintModality': 'On-site, hybrid, or remote',
    'editor.hintSalary': 'Salary range (optional)',
    'editor.hintSalaryConfidential': 'If enabled, candidates will not see the amount',
    'editor.hintExperience': 'Minimum years of experience',
    'editor.hintLocation': 'City or work location',
    'editor.hintSkillsHard': 'One per line, e.g. JavaScript, Python',
    'editor.hintSkillsSoft': 'One per line, e.g. Teamwork',
    'common.errorUnknown': 'Unknown error',
    'common.confirmDelete': 'Confirm deletion',
    'modal.cancel': 'Cancel',
    'modal.cannotUndo': 'This action cannot be undone.',
    'modal.delete': 'Delete',
    'modal.deleteVacancyConfirm': 'Delete vacancy "{{title}}"?',
    'modal.deleteQuestionConfirm': 'Delete this question?',
    'modal.savingChanges': 'Saving changes...',
    'modal.savedSuccess': 'Changes saved successfully.',
    'modal.logoutTitle': 'Log out',
    'modal.logoutMessage': 'Are you sure you want to log out?',
    'modal.logoutConfirm': 'Yes, log out',
    'modal.vacancySavedSuccess': 'Vacancy saved successfully',
    'modal.questionsSavedSuccess': 'Questions saved successfully',
    'modal.done': 'Done',
    'editor.statusDraft': 'Draft',
    'editor.statusActive': 'Active',
    'editor.statusPaused': 'Paused',
    'editor.statusClosed': 'Closed',
    'questions.deleteSuccess': 'Question deleted successfully.',
    'questions.deleteError': 'Error deleting question. Please try again.',
    'questions.saving': 'Saving questions...',
    'questions.saveEdit': 'Save changes',
    'questions.cancelEdit': 'Cancel',
    'questions.editQuestion': 'Edit this question',
    'questions.deleteQuestion': 'Delete this question',
    'questions.saveQuestions': 'Save Questions',
    'questions.saveChanges': 'Save Changes',
    'questions.confirmDelete': 'Confirm Deletion',
    'questions.deleteConfirmMessage': 'Are you sure you want to delete this question?',
    'questions.deleting': 'Deleting...',
    'questions.generateAnother': 'Generate another question',
    'workspace.intro': 'Review candidates who applied to this vacancy and their match scores.',
    'workspace.compatibilityBreakdown': 'Compatibility breakdown',
    'workspace.candidateAnswers': 'Candidate answers',
    'workspace.noCandidates': 'No candidates yet',
    'workspace.noCandidatesDesc': 'Candidates will appear here when they apply to your vacancy.',
    'workspace.emptyHint': 'Share the vacancy link to receive applications.',
    'workspace.deleteSuccess': 'Application removed.',
    'workspace.deleteError': 'Error removing application.',
    'workspace.deleteConfirmMessage': 'Remove {{name}}\'s application? This cannot be undone.',
    'workspace.confirmDelete': 'Confirm deletion',
    'workspace.tooltipDelete': 'Remove application',
    'workspace.deleteWarning': 'This action cannot be undone and will remove all data associated with this candidate for this vacancy.',
    'common.errorLoad': 'Could not load. Check your connection and try again.',
    'common.errorSave': 'Could not save. Check your connection and try again.',
    'vacancy.saved': 'Vacancy saved successfully',
    'editor.statusLabel': 'Status',
    'editor.savingDraft': 'Saving draft...',
    'editor.publishing': 'Publishing vacancy...',
    'editor.pausing': 'Pausing vacancy...',
    'editor.closing': 'Closing vacancy...',
    'editor.saving': 'Saving...',
    'editor.saveChanges': 'Save Changes',
    'editor.successDraft': 'Draft saved',
    'editor.successPublished': 'Vacancy published',
    'editor.successPaused': 'Vacancy paused',
    'editor.successClosed': 'Vacancy closed',
    'editor.loadSuccess': 'Data loaded correctly. You can now edit the vacancy.',
    'editor.copyUrlPrompt': 'Copy the URL:',
    'editor.placeholderSoftSkills': 'E.g., Communication\nLeadership',
    'editor.errorSaveHint': 'Error saving.',
    'editor.positionTitle': 'Position title',
    'editor.vacancySummary': 'Vacancy summary',
    'editor.intro': 'Review and complete the data. Choose the status (Draft, Active, Paused or Closed) and save. Then you can configure the interview questions.',
    'editor.softSkills': 'Soft skills',
    'editor.location': 'Location',
    'editor.modality': 'Modality',
    'editor.modalityPresencial': 'On-site',
    'editor.modalityRemoto': 'Remote',
    'editor.modalityHibrido': 'Hybrid',
    'editor.salaryMin': 'Minimum Salary',
    'editor.salaryMax': 'Maximum Salary',
    'editor.currency': 'Currency',
    'editor.salaryConfidential': 'Show salary as "Confidential" to candidate',
    'editor.experienceYears': 'Required Experience (Years)',
    'editor.hardSkills': 'Hard Skills',
    'editor.placeholderTitle': 'E.g. Senior Full Stack Developer',
    'editor.placeholderSummary': 'Briefly describe the vacancy...',
    'editor.placeholderLocation': 'E.g. Mexico City, CDMX',
    'editor.placeholderSalaryMin': 'E.g. 30000',
    'editor.placeholderSalaryMax': 'E.g. 45000',
    'editor.placeholderSkills': 'One per line. E.g. Python, SQL, AWS',
    'editor.noTitle': 'No Title',
    'editor.waitingAnalysis': 'Waiting for AI analysis results and backend connection.',
    'editor.copyUrl': 'Copy URL',
    'editor.copied': 'Copied!',
    'editor.copyUrlDraft': 'Publish the vacancy before sharing the link: while it is a draft, anyone who opens it will see it is unavailable.',
    'editor.errorNoId': 'Error: No vacancy ID provided',
    'editor.loadingVacancy': 'Loading vacancy data...',
    'editor.errorLoadHint': 'Error loading:',
    'questions.generatedCount': 'Generated Questions ({{count}})',
    'questions.difficulty': 'Difficulty',
    'questions.loading': 'Loading questions...',
    'questions.questionType': 'Question type:',
    'questions.typeLabel': 'Type:',
    'questions.typeTechnical': 'Technical',
    'questions.typeBehavioral': 'Behavioral',
    'questions.typeCulture': 'Culture',
    'questions.difficultyEasy': 'Easy',
    'questions.difficultyMedium': 'Medium',
    'questions.difficultyHard': 'Hard',
    'questions.placeholderQuestion': 'Write the question...',
    'questions.noQuestionsYet': 'No questions generated yet. Questions are generated automatically when you save the vacancy. If you just saved, wait a few seconds and refresh the page.',
    'questions.regenerating': 'Regenerating questions...',
    'questions.regenerateSuccess': '✅ New questions generated. Please review and approve if you like them.',
    'questions.regenerateError': 'Error regenerating questions.',
    'questions.addQuestionError': 'Error generating new question.',
    'questions.deleteErrorShort': 'Error deleting question.',
    'questions.savingChanges': 'Saving changes...',
    'questions.updateError': 'Error updating question.',
    'questions.loadError': 'Error loading questions. Please try again.',
    'editor.autoSaved': 'Changes saved',
    'editor.dataLoadedBetter': 'We have extracted the data from the document. Review and correct as needed before publishing.',
    'login.welcome': 'Welcome',
    'login.subtitle': 'AI Recruitment Platform',
    'login.continueGoogle': 'Continue with Google',
    'login.connecting': 'Connecting...',
    'login.accessingPanel': 'Accessing Panel...',
    'login.errorServer': 'Error processing server response.',
    'login.footer': '© AI Recruiter',
    'upload.title': 'Upload your Job Description',
    'upload.subtitle': 'Upload the job description (PDF or Word) to get started',
    'upload.nextStep': 'You will then review the data extracted by AI and publish the vacancy.',
    'upload.formatsLabel': 'Accepted formats:',
    'upload.clickInstruction': 'Click on the sphere to select',
    'upload.dropHere': 'Drag and drop your JD (PDF/DOCX) here',
    'upload.unsupportedFormat': 'Invalid format. Use a PDF or DOCX file.',
    'upload.uploading': 'Uploading and analyzing "{{name}}". This may take a few seconds...',
    'upload.analysisComplete': 'AI analysis complete! Loading editor...',
    'upload.stepUploading': 'Uploading file...',
    'upload.stepExtracting': 'Extracting text...',
    'upload.stepAnalyzing': 'Analyzing with AI...',
    'upload.stepExtractInfo': 'Extracting information from JD',
    'upload.stepAnalyzingIA': 'Analyzing with AI',
    'upload.stepSaving': 'Saving results',
    'upload.timeoutMessage': 'The process is taking longer than expected. Check the panel to see if the vacancy was created.',
    'upload.analyzingDocument': 'Analyzing document',
    'upload.analyzingDocumentMessage': 'We are analyzing the document to extract the vacancy data.',
    'upload.analyzingShort': 'One moment...',
    'upload.extractionComplete': 'Processing successful!',
    'upload.extractionCompleteMessage': 'We have finished processing the document correctly. Review the extracted data before publishing.',
    'upload.duplicateModalTitle': 'Update existing vacancy?',
    'upload.duplicateModalMessage': 'You already have a vacancy with the same title: "{{title}}". Do you want to update that vacancy with this JD or keep the new one we just created?',
    'upload.duplicateOverwrite': 'Update that vacancy',
    'upload.duplicateKeepNew': 'Keep the new one',
    'upload.errorAnalysis': 'Error in AI analysis.',
    'upload.timeoutLong': 'The process is taking longer than expected. Please check the vacancies panel to see if the vacancy was created correctly.',
    'upload.vacancyLimitReached': 'You have reached the maximum number of vacancies for your plan. Set a vacancy to Closed status or upgrade your plan to upload another.',
    'upload.gcsUnavailable': 'Could not save the file to storage. Check GCS configuration on the server (credentials and bucket name).',
    'common.failed': 'Failed',
    'common.unexpectedError': 'Unexpected error',
    'common.errorUpdate': 'Error updating',
    // ──── Premium / Checkout ────
    'premium.title': 'Premium Subscription',
    'premium.subtitle': 'Power up your recruitment processes with advanced AI.',
    'premium.period': '/ month',
    'premium.feature1': 'Unlimited Resume Analysis',
    'premium.feature2': 'AI-assisted Interviews',
    'premium.feature3': 'Detailed Candidate Reports',
    'premium.feature4': 'Priority Support',
    'premium.checkoutTitle': 'Complete Purchase',
    'premium.cardInstructions': 'Enter your card details',
    'premium.payNow': 'Pay Now',
    'premium.paying': 'Processing...',
    'premium.paid': 'Paid!',
    'premium.success': 'Thank you very much for your purchase!',
    'premium.errorInit': 'Could not initialize payment. Check your connection.',
    'premium.errorPayment': 'Payment error: {{message}}',
    'premium.errorConfirm': 'Could not confirm payment with the server.',
    // ──── Layout / Sidebar ────
    'layout.expandMenu': 'Expand menu',
    'layout.collapseMenu': 'Collapse menu',
    'layout.themeDefault': 'Classic Blue',
    'layout.themeViolet': 'Violet',
    'layout.themeBlack': 'Pure Black',
    'layout.themeLightPure': 'Pure White',
    'layout.themeVioletInverted': 'Violet Inverted',
    'layout.notifications': 'Notifications',
    'layout.notificationsHeader': 'Notifications',
    'layout.markAllRead': 'Mark all as read',
    'layout.noNotifications': 'No notifications',
    // ──── Settings – Notifications tab ────
    'settings.notifications.description': 'Notification summary. When a new application is submitted to one of your vacancies, it will appear here with details.',
    'settings.notifications.markAllRead': 'Mark all as read',
    'settings.notifications.none': 'No notifications.',
    'settings.notifications.vacancyLabel': 'Vacancy: ',
    'settings.notifications.candidateLabel': 'Candidate: ',
    'settings.notifications.emailLabel': 'Email: ',
    'settings.profile.photoAlt': 'Profile photo',
    // ──── Onboarding ────
    'onboarding.step1Title': 'Welcome to AI Recruiter',
    'onboarding.step1Desc': 'Your intelligent platform to manage the recruitment process with AI. Start by creating your first vacancy.',
    'onboarding.step2Title': '1. Upload your Job Description',
    'onboarding.step2Desc': 'Upload a PDF or DOCX file with the job description. Our AI will automatically extract all relevant information.',
    'onboarding.step3Title': '2. Edit and Customize',
    'onboarding.step3Desc': 'Review and adjust the data extracted by AI. You can modify skills, requirements, salary, and more job details.',
    'onboarding.step4Title': '3. Configure Questions',
    'onboarding.step4Desc': 'Customize the AI-assisted interview questions. You can add, edit, or delete questions to suit your needs.',
    'onboarding.step5Title': '4. Publish your Vacancy',
    'onboarding.step5Desc': 'Change the status of your vacancy to ACTIVE so candidates can find and apply. You will get a public link to share.',
    'onboarding.step6Title': '5. Review Candidates',
    'onboarding.step6Desc': 'In the Workspace you can see all candidates who applied, their match scores, AI analysis, and compare profiles easily.',
    'onboarding.step7Title': 'Ready to Start!',
    'onboarding.step7Desc': 'Now you know the main features. Click the button to create your first vacancy and start recruiting with AI.',
    'onboarding.skip': 'Skip',
    'onboarding.previous': 'Previous',
    'onboarding.next': 'Next',
    'onboarding.start': 'Get Started',
    // ──── Upload Circle ────
    'uploadCircle.defaultText': 'Drop your JD here',
    'uploadCircle.successText': 'Sent!',
    // ──── Workspace ────
    'workspace.loading': 'Calculating Match Scores...',
    'workspace.headerCandidate': 'Candidate',
    'workspace.headerStatus': 'Status',
    'workspace.headerExp': 'Exp.',
    'workspace.headerSkills': 'Skills',
    'workspace.headerMatch': 'MATCH',
    'workspace.headerAction': 'Action',
    'workspace.years': 'years',
    'workspace.contactLabel': 'Contact:',
    'workspace.locationLabel': 'Location:',
    'workspace.educationLabel': 'Education:',
    'workspace.experienceLabel': 'Experience:',
    'workspace.statusLabel': 'Status:',
    'workspace.iaAnalysis': 'AI Analysis',
    'workspace.experience': 'Experience',
    'workspace.technicalSkills': 'Technical Skills',
    'workspace.candidateResponses': 'Candidate Responses',
    'workspace.clickToSeeAnswers': '(Click to see answers)',
    'workspace.softSkills': 'Soft Skills',
    'workspace.education': 'Education',
    'workspace.questionLabel': 'Question {{num}}:',
    'workspace.answerLabel': 'Answer:',
    'workspace.noAnswers': 'No answers available for this candidate.',
    'workspace.noBreakdown': 'The compatibility breakdown is not available for this candidate. It may still be calculating.',
    'workspace.errorCouldNotDelete': 'Could not delete',
    'workspace.errorNetwork': 'Network error',
    'workspace.errorLoadCandidates': 'Could not load candidates list.',
    'workspace.notScored': 'Not scored',
    'workspace.estado.postulado': 'Applied',
    'workspace.estado.en_revision': 'In review',
    'workspace.estado.entrevistado': 'Interviewed',
    'workspace.estado.seleccionado': 'Selected',
    'workspace.estado.descartado': 'Not selected',
    'workspace.errorEstado': 'Could not change the candidate status.',
    'workspace.closeProcess': 'Close process',
    'workspace.closeProcessHint': 'Closes the vacancy, hires whoever you selected and turns down the rest.',
    'workspace.closing': 'Closing...',
    'workspace.closeConfirmHired': 'You are about to hire {{name}} and turn down {{count}} candidate(s) still in process. The vacancy will be closed. Continue?',
    'workspace.closeConfirmNoHire': 'You have not selected anyone. {{count}} candidate(s) will be turned down and the vacancy closed without hiring. Continue?',
    'workspace.closeDone': 'Process closed. {{count}} application(s) were turned down.',
    'workspace.errorClose': 'Could not close the process.',
    'workspace.notScoredHint': 'They applied, but the AI has not scored them yet.',
    'workspace.certificationsLabel': 'Certifications:',
    // ──── Metrics ────
    'metrics.loading': 'Loading metrics...',
    'metrics.errorLoading': 'Error loading metrics',
    'metrics.noDataTitle': 'No data yet',
    'metrics.noDataDesc': 'Metrics will appear when candidates apply.',
    'metrics.totalCandidates': 'Total Candidates',
    'metrics.applicationsReceived': 'applications received',
    'metrics.avgScore': 'Avg Score',
    'metrics.avgMatch': 'avg match',
    'metrics.topScore': 'Top Score',
    'metrics.bestCandidate': 'best candidate',
    'metrics.viableCandidates': 'Viable Candidates',
    'metrics.talentQuality': 'Talent Quality',
    'metrics.candidates': 'candidates',
    'metrics.candidatesInRange': 'Candidates in range',
    'metrics.generalMatch': 'General Match',
    'metrics.weight': 'Weight',
    'metrics.candidateSource': 'Candidate Source',
    'metrics.avgScoreLabel': 'Avg Score',
    'metrics.experienceLevel': 'Experience',
    'metrics.keySkills': 'Key Skills',
    'metrics.notEnoughData': 'Not enough data',
    'metrics.topScoreMentions': 'mentions in top scores',
    'metrics.frequencyNote': 'Frequency in candidates with score > 75%',
    'metrics.educationTitle': 'Education',
    'metrics.notSpecified': 'Not specified',
    // ──── Metrics – Radar categories ────
    'metrics.radar.experience': 'Experience',
    'metrics.radar.technicalSkills': 'Tech. Skills',
    'metrics.radar.responses': 'Responses',
    'metrics.radar.softSkills': 'Soft Skills',
    'metrics.radar.education': 'Education',
    // ──── Metrics – Education labels ────
    'metrics.edu.bachelors': 'Bachelor\'s',
    'metrics.edu.masters': 'Master\'s',
    'metrics.edu.doctorate': 'Doctorate',
    'metrics.edu.certifications': 'Certifications',
    'metrics.edu.other': 'Other',
    // ──── Candidate Flow ────
    'candidate.default': 'Candidate',
    'candidate.loading': 'Loading...',
    'candidate.expandMenu': 'Expand menu',
    'candidate.collapseMenu': 'Collapse menu',
    'layout.changeLanguage': 'Change Language',
    'candidate.switchToEnglish': 'Switch to English',
    'candidate.switchToSpanish': 'Cambiar a Español',
    'candidate.logout': 'Logout',
    // Step 1 – CV Upload
    'candidate.step1.title': 'Upload your CV',
    'candidate.step1.description': 'Drag your CV file here or click to select it',
    'candidate.step1.formats': 'Accepted formats:',
    'candidate.step1.dragHere': 'Drag your CV here',
    'candidate.step1.cvProcessed': 'CV processed',
    'candidate.step1.stepsTitle': 'Process Steps',
    'candidate.step1.stepsDescription': 'Complete the following requirements to apply for this vacancy:',
    'candidate.step1.uploadCV': 'Upload your updated CV',
    'candidate.step1.verifyInfo': 'Verify extracted information',
    'candidate.step1.recordVideo': 'Record a presentation video',
    'candidate.step1.answerQuestions': 'Answer vacancy questions',
    'candidate.step1.linkCalendar': 'Link your Google calendar',
    'candidate.step1.scheduleInterview': 'Schedule interview day and time',
    // Step Review Data
    'candidate.review.analyzingTitle': 'Analyzing Profile...',
    'candidate.review.analyzingSubtitle': 'We are organizing the information extracted from your CV.',
    'candidate.review.title': 'Profile Validation',
    'candidate.review.subtitle': 'Check that the extracted information is correct. Click on any field to edit it.',
    'candidate.review.personalInfo': 'Personal Information',
    'candidate.review.fullName': 'Full Name',
    'candidate.review.fullNamePlaceholder': 'Your name',
    'candidate.review.email': 'Professional Email',
    'candidate.review.phone': 'Contact Phone',
    'candidate.review.profileSummary': 'Profile Summary',
    'candidate.review.profileSummaryPlaceholder': 'Brief description of your career...',
    'candidate.review.vacancySource': 'How did you hear about this vacancy?',
    'candidate.review.selectOption': 'Select an option',
    'candidate.review.referred': 'Referred / Acquaintance',
    'candidate.review.jobBoard': 'Job Board (OCC, CompuTrabajo, etc.)',
    'candidate.review.companyWebsite': 'Company Website',
    'candidate.review.socialNetworks': 'Social Networks (Facebook, Instagram, X)',
    'candidate.review.university': 'University / School',
    'candidate.review.other': 'Other',
    'candidate.review.workExperience': 'Work Experience',
    'candidate.review.jobTitle': 'Job Title / Position',
    'candidate.review.company': 'Company',
    'candidate.review.period': 'Period',
    'candidate.review.start': 'Start',
    'candidate.review.end': 'End',
    'candidate.review.location': 'Location',
    'candidate.review.responsibilities': 'Responsibilities and Achievements',
    'candidate.review.responsibilitiesPlaceholder': 'One responsibility per line',
    'candidate.review.education': 'Education',
    'candidate.review.degree': 'Degree / Academic Title',
    'candidate.review.institution': 'University / School Institution',
    'candidate.review.projects': 'Highlighted Projects',
    'candidate.review.projectName': 'Project Name',
    'candidate.review.technologies': 'Technologies Used',
    'candidate.review.projectDescription': 'Project Description',
    'candidate.review.skills': 'Skills',
    'candidate.review.languages': 'Languages',
    'candidate.review.certifications': 'Certifications',
    'candidate.review.certName': 'Certification Name',
    'candidate.review.certIssuer': 'Issuing Entity',
    'candidate.review.saving': '⏳ Saving...',
    'candidate.review.confirmed': '✓ Data Confirmed!',
    'candidate.review.confirmData': 'Confirm Data',
    // Step 2 – Video Recorder
    'candidate.video.prepTitle': 'Get ready for your video presentation',
    'candidate.video.prepSubtitle': 'Briefly explain your experience and why you are interested in this position',
    'candidate.video.lighting': 'Lighting',
    'candidate.video.lightingTip': 'Make sure you are in a well-lit place, preferably with light facing you.',
    'candidate.video.sound': 'Sound',
    'candidate.video.soundTip': 'Find a quiet environment so we can hear you clearly.',
    'candidate.video.framing': 'Framing',
    'candidate.video.framingTip': 'Place the camera at eye level and maintain a comfortable distance.',
    'candidate.video.naturalness': 'Naturalness',
    'candidate.video.naturalnessTip': "Don't worry about being perfect, be yourself. We are excited to hear from you.",
    'candidate.video.start': 'Start',
    'candidate.video.recordingTitle': 'Interview Recording',
    'candidate.video.cameraPreview': 'Camera preview',
    'candidate.video.startRecording': 'Start Recording',
    'candidate.video.stopRecording': 'Stop Recording',
    'candidate.video.uploadRecorded': 'Upload Recorded Video',
    'candidate.video.downloadVideo': 'Download Video',
    'candidate.video.uploadSuccess': 'Video uploaded successfully!',
    'candidate.video.uploading': 'Uploading video...',
    'candidate.video.or': 'or',
    'candidate.video.uploadOwn': 'Upload Your Own Video',
    'candidate.video.selectVideo': 'Select Video',
    'candidate.video.selectedFile': 'Selected file: {{name}}',
    'candidate.video.uploadFile': 'Upload File',
    // Step 3 – Questions
    'candidate.questions.title': 'AI-Assisted Interview',
    'candidate.questions.selectVacancy': 'Select the vacancy you wish to apply for:',
    'candidate.questions.loadingVacancies': 'Loading available vacancies...',
    'candidate.questions.noVacancies': 'No available vacancies found.',
    'candidate.questions.selectThis': 'Select This Vacancy',
    'candidate.questions.yearsExp': '{{years}}+ years',
    'candidate.questions.answerPlaceholder': 'Write your answer here...',
    'candidate.questions.charCount': '{{count}} / {{max}} characters',
    'candidate.questions.shortAnswerHint': 'A more detailed answer scores better.',
    'candidate.questions.preparingInterview': 'Preparing your personalized interview...',
    'candidate.questions.answerInstructions': 'Answer the following questions to complete your interview:',
    'candidate.questions.changeVacancy': 'Change Vacancy',
    'candidate.questions.salaryLabel': 'What is your salary expectation?',
    'candidate.questions.salaryPlaceholder': 'E.g.: $50,000 - $60,000 MXN monthly',
    'candidate.questions.salaryHint': 'You can specify a range or a specific amount',
    'candidate.questions.submit': 'Submit Answers',
    'candidate.questions.incompleteAlert': 'Answer every question to continue.',
    'candidate.questions.errorSave': 'Error saving answers: {{error}}',
    'candidate.questions.noQuestions': 'Could not get questions for this vacancy.',
    'candidate.questions.pendingReview': 'Questions for this vacancy are being reviewed by the recruiter. Please try again later.',
    'candidate.questions.rejected': 'Questions for this vacancy were rejected. The recruiter must generate new questions.',
    'candidate.questions.checkConnection': 'Check your connection or try again.',
    'candidate.questions.tryAgain': 'Try Again',
    // Step 4 – Calendar
    'candidate.calendar.verifying': 'Verifying calendar access...',
    'candidate.calendar.processing': 'Processing Connection...',
    'candidate.calendar.processingMessage': 'We are confirming the connection with Google. Please wait a moment, this may take a few seconds.',
    'candidate.calendar.connectTitle': 'Connect your Calendar for Interviews',
    'candidate.calendar.connectMessage': 'Connect your Google Calendar so we can see your availability and send you interview invitations.',
    'candidate.calendar.connectButton': 'Connect Google Calendar',
    'candidate.calendar.loadingUser': 'Loading User...',
    'candidate.calendar.waitingAuth': 'Waiting for Firebase authentication to get your user ID...',
    'candidate.calendar.errorConnect': 'Error connecting to Google Calendar. Please try again.',
    'candidate.calendar.errorRetry': 'Error: {{error}}. Please try again.',
    // Step 5 – Calendar Success
    'candidate.calendarSuccess.title': 'Calendar Connected Successfully',
    'candidate.calendarSuccess.message': 'Thank you for connecting your Google Calendar. We can now verify your availability for future interviews.',
    'candidate.calendarSuccess.errorTitle': 'Calendar Connection Failed',
    'candidate.calendarSuccess.errorDetail': 'Error Detail:',
    'candidate.calendarSuccess.errorHint': 'Please try again. Ensure you grant all permissions requested by Google.',
    'candidate.calendarSuccess.defaultError': 'An unknown error occurred during connection.',
    'candidate.calendarSuccess.back': 'Back to home',
    // Step 6 – Schedule Interview
    'candidate.schedule.title': 'Schedule Interview',
    'candidate.schedule.description': 'Select an available day and time for your interview.',
    'candidate.schedule.queryingTimes': 'Querying available times...',
    'candidate.schedule.notice': 'Notice:',
    'candidate.schedule.reconnect': 'Reconnect Google Calendar',
    'candidate.schedule.fallbackHint': 'You can choose day and time below.',
    'candidate.schedule.selectDay': 'Select a day:',
    'candidate.schedule.availableTimes': 'Available times:',
    'candidate.schedule.noSlots': 'No times available for this day.',
    'candidate.schedule.selected': 'You have selected: {{date}} at {{time}}',
    'candidate.schedule.confirm': 'Confirm Interview',
    'candidate.schedule.scheduling': 'Scheduling...',
    'candidate.schedule.processingTitle': 'Processing your appointment...',
    'candidate.schedule.processingMessage': 'We are scheduling your interview and sending the corresponding invitations.',
    'candidate.schedule.completedTitle': 'Application Completed!',
    'candidate.schedule.completedMessage': 'You have successfully completed all steps of your application.',
    'candidate.schedule.proposedTime': 'Proposed Time for Interview',
    'candidate.schedule.pendingValidation': 'The recruiter will review your profile and, if viable, the interview will be confirmed via Google Meet. You will receive a notification by email. The appointment may be subject to changes according to the recruiter\'s availability.',
    'candidate.schedule.thanks': 'Thank you for applying! We wish you much success.',
    'candidate.schedule.applyOther': 'Apply to another vacancy',
    'candidate.schedule.finishProcess': 'Finish process',
    'candidate.schedule.exploreOther': 'Explore other vacancies',
    'candidate.schedule.exploreMessage': 'You can still apply to other positions that match your profile:',
    'candidate.schedule.backConfirmation': 'Back to my confirmation',
    'candidate.schedule.authError': 'Authentication error. Please restart the session.',
    'candidate.schedule.selectTime': 'Please select a time.',
    'candidate.schedule.tokenNotFound': 'Session token not found.',
    'candidate.schedule.errorSchedule': 'Error scheduling the interview',
    'candidate.schedule.errorScheduleAlert': 'Error scheduling interview: {{error}}',
    'candidate.schedule.mustAuth': 'You must be authenticated to connect your calendar.',
    'candidate.schedule.errorQueryAvailability': 'Failed to query availability:',
    'candidate.schedule.errorFetchAvailability': 'Error fetching availability.',
    // Stepper
    'candidate.stepper.verifyingSession': 'Verifying session...',
    'candidate.stepper.loadingSession': 'Loading user session...',
    'candidate.stepper.processTitle': 'Application Process',
    'candidate.stepper.processDescription': 'Complete each of these 5 steps to boost your professional application',
    'candidate.stepper.startProcess': 'Start Process',
    'candidate.stepper.previous': 'Previous',
    'candidate.stepper.previousStep': 'Previous step',
    'candidate.stepper.reviewLabel': 'Review',
    'candidate.stepper.interviewLabel': 'Interview',
    'candidate.stepper.scheduleLabel': 'Schedule',
    'candidate.stepper.errorAuth': 'Error: User not authenticated',
    'candidate.stepper.errorUploadCV': 'Error uploading CV: {{error}}',
    'candidate.stepper.errorExtractCV': 'Your CV was saved, but we could not read it automatically: {{error}}. Please try again in a few minutes.',
    'candidate.stepper.errorExtractCVUnknown': 'the reading service is unavailable',
    'candidate.stepper.alreadyApplied': 'You already applied to this vacancy on {{date}}. If you continue, you will replace your previous CV, video and answers. Continue?',
    'candidate.stepper.alreadyResolved': 'This vacancy is already resolved and your application is "{{estado}}". No further changes can be sent.',
    'candidate.stepper.previousInProgress': 'You already applied to this vacancy on {{date}}. Status: {{estado}}. Sending again will replace your previous submission.',
    'candidate.stepper.previousResolved': 'This vacancy is already resolved. Your application ended as: {{estado}}.',
    'candidate.stepper.seeOtherVacancies': 'See other vacancies',
    'candidate.stepper.errorUploadVideo': 'Error uploading video: {{error}}',
    'candidate.stepper.errorConfirmData': 'Error confirming data: {{error}}',
    'candidate.stepper.processingSuccess': 'Processing Successful!',
    'candidate.stepper.processingSuccessMessage': 'We have successfully finished processing your information.',
    'candidate.stepper.confirmingProfile': 'Confirming Profile',
    'candidate.stepper.confirmingProfileMessage': 'Validating your professional data for the next step.',
    'candidate.stepper.sendingAnswers': 'Sending Answers',
    'candidate.stepper.sendingAnswersMessage': 'We are processing your interview for the recruiter.',
    'candidate.stepper.processingCV': 'Processing your CV',
    'candidate.stepper.processingCVMessage': 'We are analyzing your career to speed up your professional application.',
    'candidate.stepper.processingVideo': 'Processing your Video',
    'candidate.stepper.processingVideoMessage': 'We are analyzing your video to evaluate your skills and competencies.',
    'candidate.stepper.cvStep1': 'Uploading CV to our servers',
    'candidate.stepper.cvStep2': 'Extracting professional information with AI',
    'candidate.stepper.cvStep3': 'Structuring candidate profile',
    'candidate.stepper.videoStep1': 'Uploading video',
    'candidate.stepper.videoStep2': 'Analyzing feelings and emotions',
    'candidate.stepper.videoStep3': 'Evaluating verbal and non-verbal communication',
    'candidate.stepper.videoStep4': 'Calculating scoring and saving results',
    // Saite Companion
    'candidate.saite.step1': "Hi! I'm Saity. Upload your CV to start, I'll be here to guide you!",
    'candidate.saite.step2': "Done! Let's check that your info is perfect before moving on.",
    'candidate.saite.step3': 'Great profile! Now get ready, let\'s record a short video interview.',
    'candidate.saite.step4': 'Excellent video! Now some quick questions to get to know you better.',
    'candidate.saite.step5': 'Almost there! Connect your calendar to schedule our chat.',
    'candidate.saite.step6': 'You did it! Choose the time that works best for your interview.',
    'candidate.review.addWorkExp': '+ Add Experience',
    'candidate.review.noWorkExp': 'No work experience was detected in your CV. You can add it manually.',
    'candidate.review.removeExperience': 'Remove this experience',
    'candidate.review.remove': 'Remove',
    'candidate.review.addEducation': '+ Add Education',
    'candidate.review.noEducation': 'No academic background was detected in your CV. You can add it manually.',
    'candidate.review.removeEducation': 'Remove this education',
    'candidate.review.details': 'Additional details',
    'candidate.review.add': '+ Add',
    'candidate.review.noSkills': 'No skills were detected. Add them manually.',
    'candidate.review.selectCategory': 'Select a category',
    'candidate.review.skillsPlaceholder': 'Skills separated by comma',
    'candidate.review.noLanguages': 'No languages were detected. Add them manually.',
    'candidate.review.languagePlaceholder': 'Language (e.g., English)',
    'candidate.review.levelPlaceholder': 'Level (e.g., B2, Advanced)',
    'candidate.review.addProject': '+ Add Project',
    'candidate.review.noProjects': 'No projects were detected. If you have relevant projects, add them here.',
    'candidate.review.removeProject': 'Remove this project',
    'candidate.review.technologiesPlaceholder': 'React, Python, PostgreSQL...',
    'candidate.review.technologiesHint': '(separated by comma)',
    'candidate.review.projectDescriptionPlaceholder': 'What did the project consist of? What was your role?',
    'candidate.review.addCertification': '+ Add Certification',
    'candidate.review.noCertifications': 'No certifications were detected. If you have relevant certificates, add them here.',
    'candidate.review.removeCertification': 'Remove this certification',
    'candidate.review.issueDate': 'Issue Date',
    'candidate.review.credentialId': 'Credential ID',
    'candidate.review.credentialPlaceholder': 'ID or verification code',
    'candidate.review.confirmSuccess': '✓ Data Confirmed!',
    'candidate.review.confirmButton': 'Confirm Data',
    'candidate.review.link': 'Link / URL',
    'candidate.review.certNamePlaceholder': 'E.g.: AWS Certified Developer',
    'candidate.review.certIssuerPlaceholder': 'E.g.: Amazon Web Services',
    'candidate.review.educationPlaceholder': 'E.g.: Systems Engineering',
    'candidate.review.institutionPlaceholder': 'Institution name',
    'candidate.review.detailsPlaceholder': 'E.g.: GPA 3.8, Graduated with Honors',
    'candidate.review.jobTitlePlaceholder': 'E.g.: Senior Developer',
    'candidate.review.companyPlaceholder': 'Company name',
    'candidate.review.locationPlaceholder': 'City, Country',
    'candidate.review.city': 'City',
    'candidate.review.state': 'State / Province',
    'candidate.review.country': 'Country',
    'candidate.review.statePlaceholder': 'State or province',
    'candidate.review.skillCategory.tecnicas': 'Technical',
    'candidate.review.skillCategory.blandas': 'Soft Skills',
    'candidate.review.skillCategory.herramientas': 'Tools',
    'candidate.review.skillCategory.programacion': 'Programming Languages',
    'candidate.review.skillCategory.frameworks': 'Frameworks & Libraries',
    'candidate.review.skillCategory.db': 'Databases',
    'candidate.review.skillCategory.devops': 'Cloud & DevOps',
    'candidate.review.skillCategory.design': 'Design',
    'candidate.review.skillCategory.management': 'Project Management',
    'candidate.review.skillCategory.others': 'Others',
    'candidate.review.source.referred': 'Referred / Acquaintance',
    'candidate.review.source.jobBoard': 'Job Board (OCC, CompuTrabajo, etc.)',
    'candidate.review.source.companyWebsite': 'Company Website',
    'candidate.review.source.socialNetworks': 'Social Networks (Facebook, Instagram, X)',
    'candidate.review.source.university': 'University / School',
    'candidate.review.source.other': 'Other',
    'candidate.video.fileSizeError': 'The selected file weighs {{size}} MB and exceeds the limit of {{max}} MB. Please select a smaller video.',
    'candidate.calendar.userNotAuth': 'User is not authenticated or is loading.',
    'candidate.calendar.tokenNotFound': 'Session token not found.',
    'candidate.questions.errorFetch': 'Could not get questions for this vacancy.',
    'candidate.questions.errorLoad': 'Error loading questions. Please try again.',
    'shared.profilePhotoAlt': 'Profile photo',
  },
};

const ThemeLanguageContext = createContext<ThemeLanguageContextValue | null>(null);

export function ThemeLanguageProvider(props: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(readTheme);
  const [lang, setLangState] = useState<LangId>(readLang);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_THEME, theme);
    } catch (_) { }
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = lang === 'es' ? 'es' : 'en';
    try {
      localStorage.setItem(STORAGE_LANG, lang);
    } catch (_) { }
  }, [lang]);

  const setTheme = useCallback((id: ThemeId) => setThemeState(id), []);
  const setLang = useCallback((id: LangId) => setLangState(id), []);

  const t = useCallback(
    (key: string, vars?: Record<string, string>) => {
      const dict = translations[lang];
      const otherLang = lang === 'es' ? 'en' : 'es';
      let s = (dict && dict[key]) ?? (translations[otherLang] && translations[otherLang][key]) ?? key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          s = s.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
        });
      }
      return s;
    },
    [lang]
  );

  const value = useMemo<ThemeLanguageContextValue>(
    () => ({ theme, setTheme, lang, setLang, t }),
    [theme, setTheme, lang, setLang, t]
  );

  return React.createElement(
    ThemeLanguageContext.Provider,
    { value: value },
    props.children
  );
}

export function useThemeLanguage(): ThemeLanguageContextValue {
  const ctx = useContext(ThemeLanguageContext);
  if (!ctx) {
    return {
      theme: 'default',
      setTheme: () => { },
      lang: 'es',
      setLang: () => { },
      t: (k: string, _v?: Record<string, string>) => k,
    };
  }
  return ctx;
}
